import React, { useState, ChangeEvent, FormEvent, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, TrashIcon, EyeIcon, XMarkIcon, CheckIcon, ChevronDownIcon, ChevronRightIcon, ExclamationTriangleIcon, PencilIcon, DocumentTextIcon, UserIcon } from '@heroicons/react/24/solid';
import { apiUrl, authFetch, authService, getImageUrl, TENANT_ID } from '../utils/api';
import { useUser } from '../contexts/UserContext';
import { P } from '../types/assistantPrivileges';

interface Unit {
  id: number;
  title: string;
  lessons: Lesson[];
  isVisible: boolean;
}

interface Lesson {
  id: number;
  title: string;
  type: 'video' | 'quiz' | 'file' | 'assignment';
  isVisible: boolean;
  order?: number;
}

interface ImportCourseSummary {
  id: string;
  name: string;
}

interface Question {
  id: number;
  text: string;
  options: string[];
  correctOptionIndex: number | null;
  type: 'MultipleChoice' | 'TrueFalse';
}

interface TeacherSubject {
  id: string;
  name: string;
  description: string;
  code: string;
  active: boolean;
  createdDate: string;
  modificationDate: string;
  gradeId: string;
  gradeName: string;
  teacherCount: number;
  courseCount: number;
}

interface Course {
  id: string;
  courseName: string;
  term: string;
  active: boolean;
  price: number;
  description: string;
  imagePath: string | null;
  modificationDate: string;
  units: any[] | null;
  students: any[];
  isOpenToAll: boolean;
  isSuggested: boolean;
  teacherId: string; // Backward compatibility - primary teacher
  subjectId: string;
  subjectName: string;
  subjectCode: string;
  teacherName: string; // Backward compatibility - primary teacher
  teacherEmail: string; // Backward compatibility - primary teacher
  gradeName: string;
  // New: Support multiple teachers
  teacherIds?: string[];
  teachers?: Array<{
    id: string;
    name: string;
    email: string;
    isPrimary: boolean;
  }>;
}

interface SidePanelContent {
  type: 'video' | 'quiz' | 'file';
  title: string;
  content: any;
}

interface LessonFile {
  fileName: string;
  fileUrl: string;
  order: number;
}

interface Attachment {
  id: number;
  title: string;
  url: string;
}

interface QuizResult {
  id: string;
  lessonId: number;
  lessonName: string;
  userName: string;
  userId: string;
  score: number;
  submittedAt: string;
  notes: string;
}

interface QuizResultsData {
  lessonId: number;
  lessonName: string;
  results: QuizResult[];
  totalSubmissions: number;
  averageScore: number;
}

interface StudentCompletionData {
  completedStudents: StudentCompletion[];
  notCompletedStudents: StudentCompletion[];
}

interface StudentCompletion {
  studentId: string;
  studentName: string;
  studentPhone: string;
  parentPhone: string;
}

// Define a type for MouseEvent
type MouseEventType = {
  stopPropagation: () => void;
};

const isTrueFalseOptionPair = (answers: any[]): boolean => {
  const texts = answers
    .map((a: any) => (a?.text ?? '').toString().trim().toLowerCase())
    .filter(Boolean);
  if (texts.length !== 2) return false;
  const set = new Set(texts);
  return (
    (set.has('true') && set.has('false')) ||
    (set.has('صح') && set.has('خطأ')) ||
    (set.has('صواب') && set.has('خطأ'))
  );
};

const mapImportedQuestionType = (q: any, answers: any[]): 'MultipleChoice' | 'TrueFalse' => {
  const raw = q?.type ?? q?.questionType ?? '';
  const normalized = raw.toString().toLowerCase().replace(/[_\s-]/g, '');
  if (normalized.includes('truefalse') || normalized === 'tf' || normalized === '1') {
    return 'TrueFalse';
  }
  if (normalized.includes('multiple') || normalized.includes('choice')) {
    return 'MultipleChoice';
  }
  if (isTrueFalseOptionPair(answers)) {
    return 'TrueFalse';
  }
  return 'MultipleChoice';
};

const CourseDetail = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSaeedAdmin, hasPrivilege } = useUser();
  const canViewStudentCounts = isSaeedAdmin();
  const canAddUnit = hasPrivilege(P.Unit_Add);
  const canEditUnit = hasPrivilege(P.Unit_Edit);
  const canDeleteUnit = hasPrivilege(P.Unit_Delete);
  const canAddLesson = hasPrivilege(P.Lesson_Add);
  const canEditLesson = hasPrivilege(P.Lesson_Edit);
  const canDeleteLesson = hasPrivilege(P.Lesson_Delete);
  const canManageEnrollment = hasPrivilege(P.Course_ManageEnrollment);
  const [activeTab, setActiveTab] = useState<'content' | 'details' | 'students'>('content');
  const [showLessonTypeModal, setShowLessonTypeModal] = useState(false);
  const [selectedUnitIndex, setSelectedUnitIndex] = useState<number | null>(null);
  const [showAddUnitModal, setShowAddUnitModal] = useState(false);
  const [newUnitTitle, setNewUnitTitle] = useState('');
  const [showVideoLessonForm, setShowVideoLessonForm] = useState(false);
  const [showQuizForm, setShowQuizForm] = useState(false);
  const [showFileLessonForm, setShowFileLessonForm] = useState(false);
  const [videoLessonTitle, setVideoLessonTitle] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [attachmentTitle, setAttachmentTitle] = useState('');
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [quizTitle, setQuizTitle] = useState('');
  const [fileLessonTitle, setFileLessonTitle] = useState('');
  const [lessonFiles, setLessonFiles] = useState<LessonFile[]>([]);
  const [newFileName, setNewFileName] = useState('');
  const [newFileUrl, setNewFileUrl] = useState('');
  const [expandedUnits, setExpandedUnits] = useState<number[]>([]);
  
  // Quiz state
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState<Question>({
    id: 1,
    text: '',
    options: ['', '', '', ''],
    correctOptionIndex: null,
    type: 'MultipleChoice'
  });

  // Import quiz-from-lesson state (for quiz creation form)
  const [importCourses, setImportCourses] = useState<ImportCourseSummary[]>([]);
  const [importCoursesLoading, setImportCoursesLoading] = useState(false);
  const [selectedImportCourseId, setSelectedImportCourseId] = useState<string>('');
  const [importUnits, setImportUnits] = useState<Unit[]>([]);
  const [selectedImportUnitId, setSelectedImportUnitId] = useState<number | null>(null);
  const [importQuizzes, setImportQuizzes] = useState<Lesson[]>([]);
  const [selectedImportQuizId, setSelectedImportQuizId] = useState<number | null>(null);
  const [importingQuiz, setImportingQuiz] = useState(false);

  // Updated course state with the API structure
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  const [subjects, setSubjects] = useState<TeacherSubject[]>([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [subjectsError, setSubjectsError] = useState<string | null>(null);
  
  // State for managing teachers
  const [availableTeachers, setAvailableTeachers] = useState<Array<{id: string; fullName: string; email: string}>>([]);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [selectedTeacherToAdd, setSelectedTeacherToAdd] = useState<string>('');

  // Add state for enrolled students
  const [enrolledStudents, setEnrolledStudents] = useState<any[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [studentsError, setStudentsError] = useState<string | null>(null);
  
  // Add state for student pagination
  const [studentCurrentPage, setStudentCurrentPage] = useState(1);
  const [studentPageSize] = useState(5); // Change page size to 5 for more obvious pagination
  const [studentTotalPages, setStudentTotalPages] = useState(1);
  const [studentTotalCount, setStudentTotalCount] = useState(0);
  
  // Add state for student filters
  const [studentFilters, setStudentFilters] = useState({
    searchTerm: '',
    name: ''
  });
  const [showEnrollStudentModal, setShowEnrollStudentModal] = useState(false);
  const [enrollStudentData, setEnrollStudentData] = useState('');
  const [isEnrollingStudent, setIsEnrollingStudent] = useState(false);
  const [enrollStudentError, setEnrollStudentError] = useState<string | null>(null);
  const [showRemoveStudentModal, setShowRemoveStudentModal] = useState(false);
  const [studentToRemove, setStudentToRemove] = useState<any | null>(null);
  const [isRemovingStudent, setIsRemovingStudent] = useState(false);
  const [searchingStudent, setSearchingStudent] = useState(false);
  const [foundStudent, setFoundStudent] = useState<any | null>(null);
  const [searchSuccessMessage, setSearchSuccessMessage] = useState<string | null>(null);

  // Initialize tab and student page from URL query params (for preserving state on back navigation)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab') as 'content' | 'details' | 'students' | null;
    const pageParam = params.get('studentPage');

    if (tabParam) {
      setActiveTab(tabParam);
    }

    if (pageParam) {
      const parsedPage = parseInt(pageParam, 10);
      if (!isNaN(parsedPage) && parsedPage > 0) {
        setStudentCurrentPage(parsedPage);
      }
    }
  }, [location.search]);

  const [loadingCurriculum, setLoadingCurriculum] = useState(false);
  const [curriculumError, setCurriculumError] = useState<string | null>(null);

  // Add edit unit state
  const [showEditUnitModal, setShowEditUnitModal] = useState(false);
  const [editUnitName, setEditUnitName] = useState('');

  // Add state for quiz results
  const [showQuizResultsModal, setShowQuizResultsModal] = useState(false);
  const [, setSelectedLessonId] = useState<number | null>(null);
  const [quizResults, setQuizResults] = useState<QuizResultsData | null>(null);
  const [loadingQuizResults, setLoadingQuizResults] = useState(false);
  const [quizResultsError, setQuizResultsError] = useState<string | null>(null);
  const [quizResultsSort, setQuizResultsSort] = useState<{ score: boolean; date: boolean }>({
    score: false,
    date: false,
  });

  // Add state for student completion status
  const [showStudentCompletionModal, setShowStudentCompletionModal] = useState(false);
  const [, setSelectedVideoLessonId] = useState<number | null>(null);
  const [studentCompletion, setStudentCompletion] = useState<StudentCompletionData | null>(null);
  const [loadingStudentCompletion, setLoadingStudentCompletion] = useState(false);
  const [studentCompletionError, setStudentCompletionError] = useState<string | null>(null);

  // Keep URL query params in sync with active tab and student page
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set('tab', activeTab);
    params.set('studentPage', String(studentCurrentPage));

    navigate({ search: params.toString() }, { replace: true });
  }, [activeTab, studentCurrentPage, location.search, navigate]);

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Maximum file size (5MB)
  const MAX_FILE_SIZE = 5 * 1024 * 1024;
  // Allowed file types
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif'];

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImageError(null);
    const file = e.target.files?.[0];
    
    if (!file) {
      setImageFile(null);
      setPreviewUrl(null);
      return;
    }

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setImageError('Please select a valid image file (JPEG, PNG, or GIF)');
      e.target.value = '';
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setImageError('Image size should be less than 5MB');
      e.target.value = '';
      return;
    }

    // Create preview URL
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    setImageFile(file);

    // Cleanup preview URL when component unmounts
    return () => URL.revokeObjectURL(objectUrl);
  };

  // Fetch teacher subjects
  const fetchSubjects = async (teacherId: string) => {
    console.log('fetchSubjects called with teacherId:', teacherId);
    setLoadingSubjects(true);
    setSubjectsError(null);
    try {
      const response = await authFetch(apiUrl(`Teacher/${teacherId}/subjects`), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      console.log('Subjects API response:', data);
      
      if (data.success) {
        console.log('Subjects loaded successfully:', data.data);
        setSubjects(data.data);
        if (data.data.length === 0) {
          setSubjectsError('لم يتم العثور على مواد لهذا المدرس');
        }
      } else {
        throw new Error(data.message || 'Failed to fetch subjects');
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
      setSubjects([]);
      setSubjectsError('حدث خطأ أثناء تحميل المواد. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoadingSubjects(false);
    }
  };

  // Fetch course data and curriculum from API
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setLoadingCurriculum(true);
      setCurriculumError(null);
      
      try {
        // First fetch course details to get teacherId
        const courseResponse = await authFetch(apiUrl(`Course/${courseId}`), {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });
        
        const courseData = await courseResponse.json();
        console.log('Course API response:', courseData);
        
        if (courseData.success) {
          // Set course data
          setCourse(courseData.data);
          
          // Fetch subjects for the primary teacher (first teacher or teacherId for backward compatibility)
          const primaryTeacherId = courseData.data.teachers && courseData.data.teachers.length > 0
            ? courseData.data.teachers.find((t: any) => t.isPrimary)?.id || courseData.data.teachers[0]?.id
            : courseData.data.teacherId;
          
          if (primaryTeacherId) {
            console.log('Found teacherId in course response:', primaryTeacherId);
            await fetchSubjects(primaryTeacherId);
          } else {
            console.log('No teacherId found in course response');
          }
        }
        
        // Then fetch curriculum data
        const response = await authFetch(apiUrl(`Course/tree?courseid=${courseId}`), {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });
        
        const data = await response.json();
        
        if (data.success) {
          // Set curriculum data
          if (data.data && data.data.units) {
            setUnits(data.data.units.map((unit: any) => ({
              id: unit.id,
              title: unit.unitName || unit.titel, // API returns both unitName and titel
              lessons: unit.lessons.map((lesson: any) => ({
                id: lesson.id,
                title: lesson.lessonName || lesson.titel, // API returns both lessonName and titel
                type: lesson.type === 'Video' ? 'video' 
                  : lesson.type === 'File' ? 'file' 
                  : lesson.type === 'Assignment' ? 'assignment'
                  : 'quiz',
                isVisible: lesson.active
              })),
              isVisible: unit.active
            })));
          }
        } else {
                  setError('فشل في تحميل تفاصيل الدورة');
        setCurriculumError('فشل في تحميل المنهج الدراسي');
        }
      } catch (error) {
              console.error('Error fetching data:', error);
      setError('فشل في تحميل بيانات الدورة');
      setCurriculumError('فشل في تحميل بيانات المنهج الدراسي');
      } finally {
        setLoading(false);
        setLoadingCurriculum(false);
      }
    };

    if (courseId) {
      fetchData();
    }
    
    // Clean up function
    return () => {
      sessionStorage.removeItem('currentCourseData');
    };
  }, [courseId]);

  // Fetch available teachers
  const fetchAvailableTeachers = useCallback(async () => {
    setLoadingTeachers(true);
    try {
      const response = await authFetch(apiUrl('Teacher'), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setAvailableTeachers(data.data.filter((teacher: any) => teacher.active).map((teacher: any) => ({
          id: teacher.id,
          fullName: teacher.fullName,
          email: teacher.email
        })));
      }
    } catch (error) {
      console.error('Error fetching teachers:', error);
      setAvailableTeachers([]);
    } finally {
      setLoadingTeachers(false);
    }
  }, []);

  // Fetch teachers when details tab is active
  useEffect(() => {
    if (activeTab === 'details' && availableTeachers.length === 0) {
      fetchAvailableTeachers();
    }
  }, [activeTab, availableTeachers.length, fetchAvailableTeachers]);

  // Handle adding a teacher to the course
  const handleAddTeacher = () => {
    if (!selectedTeacherToAdd || !course) return;
    
    // Check if teacher is already added
    const currentTeacherIds = course.teacherIds || (course.teacherId ? [course.teacherId] : []);
    if (currentTeacherIds.includes(selectedTeacherToAdd)) {
      setSaveMessage({
        type: 'error',
        text: 'هذا المعلم مضاف بالفعل للدورة'
      });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    // Check if already have 2 teachers
    if (currentTeacherIds.length >= 2) {
      setSaveMessage({
        type: 'error',
        text: 'يمكن تعيين معلمين فقط لكل دورة'
      });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    const teacherToAdd = availableTeachers.find(t => t.id === selectedTeacherToAdd);
    if (!teacherToAdd) return;

    // Update course with new teacher
    const newTeacherIds = [...currentTeacherIds, selectedTeacherToAdd];
    
    // Build new teachers array
    let newTeachers: Array<{id: string; name: string; email: string; isPrimary: boolean}> = [];
    
    // If we have existing teachers, keep them
    if (course.teachers && course.teachers.length > 0) {
      newTeachers = [...course.teachers];
    } else if (course.teacherId && course.teacherName) {
      // Create from backward compatibility fields
      newTeachers = [{
        id: course.teacherId,
        name: course.teacherName,
        email: course.teacherEmail || '',
        isPrimary: true
      }];
    }
    
    // Add the new teacher (not primary if there's already a primary)
    newTeachers.push({
      id: teacherToAdd.id,
      name: teacherToAdd.fullName,
      email: teacherToAdd.email,
      isPrimary: newTeachers.length === 0 // Only primary if it's the first teacher
    });

    setCourse({
      ...course,
      teacherIds: newTeacherIds,
      teachers: newTeachers,
      // Update backward compatibility fields
      teacherId: newTeacherIds[0],
      teacherName: newTeachers.find(t => t.isPrimary)?.name || newTeachers[0]?.name || course.teacherName,
      teacherEmail: newTeachers.find(t => t.isPrimary)?.email || newTeachers[0]?.email || course.teacherEmail
    });

    setSelectedTeacherToAdd('');
  };

  // Handle removing a teacher from the course
  const handleRemoveTeacher = (teacherId: string) => {
    if (!course) return;

    const currentTeacherIds = course.teacherIds || (course.teacherId ? [course.teacherId] : []);
    
    // Don't allow removing if it's the only teacher
    if (currentTeacherIds.length <= 1) {
      setSaveMessage({
        type: 'error',
        text: 'يجب أن يكون هناك معلم واحد على الأقل للدورة'
      });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    // Remove teacher
    const newTeacherIds = currentTeacherIds.filter(id => id !== teacherId);
    const newTeachers = (course.teachers || []).filter(t => t.id !== teacherId);

    // If we removed the primary teacher, make the first remaining teacher primary
    if (newTeachers.length > 0 && !newTeachers.some(t => t.isPrimary)) {
      newTeachers[0].isPrimary = true;
    }

    setCourse({
      ...course,
      teacherIds: newTeacherIds,
      teachers: newTeachers,
      // Update backward compatibility fields
      teacherId: newTeacherIds[0],
      teacherName: newTeachers.find(t => t.isPrimary)?.name || newTeachers[0]?.name || '',
      teacherEmail: newTeachers.find(t => t.isPrimary)?.email || newTeachers[0]?.email || ''
    });
  };

  // Sample units and lessons
  const [units, setUnits] = useState<Unit[]>([
    {
      id: 1,
      title: 'Unit 1: Introduction to Numbers',
      lessons: [
        { id: 1, title: 'Lesson 1: Natural Numbers', type: 'video', isVisible: true },
        { id: 2, title: 'Lesson 2: Integers', type: 'video', isVisible: true },
        { id: 3, title: 'Unit 1 Quiz', type: 'quiz', isVisible: true },
      ],
      isVisible: true
    },
    {
      id: 2,
      title: 'Unit 2: Basic Operations',
      lessons: [
        { id: 4, title: 'Lesson 1: Addition and Subtraction', type: 'video', isVisible: true },
        { id: 5, title: 'Lesson 2: Multiplication and Division', type: 'video', isVisible: true },
      ],
      isVisible: true
    },
  ]);

  const [showSidePanel, setShowSidePanel] = useState(false);
  const [sidePanelContent, setSidePanelContent] = useState<SidePanelContent | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [newAttachment, setNewAttachment] = useState<Attachment>({ id: 1, title: '', url: '' });

  const [showDeleteUnitModal, setShowDeleteUnitModal] = useState(false);
  const [showDeleteLessonModal, setShowDeleteLessonModal] = useState(false);
  const [showHideUnitModal, setShowHideUnitModal] = useState(false);
  const [showHideLessonModal, setShowHideLessonModal] = useState(false);
  const [unitToAction, setUnitToAction] = useState<{ id: number, index: number } | null>(null);
  const [lessonToAction, setLessonToAction] = useState<{ unitIndex: number, lessonId: number } | null>(null);

  const toggleUnitExpand = (unitId: number) => {
    if (expandedUnits.includes(unitId)) {
      setExpandedUnits(expandedUnits.filter(id => id !== unitId));
    } else {
      setExpandedUnits([...expandedUnits, unitId]);
    }
  };

  const handleAddUnit = async () => {
    if (!newUnitTitle.trim()) {
      setSaveMessage({
        type: 'error',
        text: 'يرجى إدخال عنوان المحاضرة'
      });
      return;
    }

    try {
      const response = await authFetch(apiUrl('Unit/add'), {
        method: 'POST',
        headers: {
          'accept': '*/*',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          unitName: newUnitTitle,
          titel: "null",
          courseId: courseId,
          active: true
        })
      });

      const data = await response.json();

      if (data.success) {
        // Show success message
        setSaveMessage({
          type: 'success',
          text: 'تم إضافة المحاضرة بنجاح'
        });

        // Refresh the course data to get the updated units
        const courseResponse = await authFetch(apiUrl(`Course/tree?courseid=${courseId}`), {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });
        
        const courseData = await courseResponse.json();
        
        if (courseData.success) {
          // Update the units state with the new data
          if (courseData.data && courseData.data.units) {
            setUnits(courseData.data.units.map((unit: any) => ({
              id: unit.id,
              title: unit.unitName || unit.titel,
              lessons: unit.lessons.map((lesson: any) => ({
                id: lesson.id,
                title: lesson.lessonName || lesson.titel,
                type: lesson.type === 'Video' ? 'video' 
                  : lesson.type === 'File' ? 'file' 
                  : lesson.type === 'Assignment' ? 'assignment'
                  : 'quiz',
                isVisible: lesson.active
              })),
              isVisible: unit.active
            })));
          }
        }

        // Clear the input and close the modal
    setNewUnitTitle('');
    setShowAddUnitModal(false);

        // Clear the success message after 3 seconds
        setTimeout(() => {
          setSaveMessage(null);
        }, 3000);
      } else {
        setSaveMessage({
          type: 'error',
          text: data.message || 'فشل في إضافة المحاضرة'
        });
      }
    } catch (error) {
      console.error('Error adding unit:', error);
      setSaveMessage({
        type: 'error',
        text: 'فشل في إضافة المحاضرة. يرجى المحاولة مرة أخرى لاحقًا.'
      });
    }
  };

  const handleAddLesson = (unitIndex: number) => {
    setSelectedUnitIndex(unitIndex);
    setShowLessonTypeModal(true);
  };

  const handleAddVideoLesson = () => {
    setShowLessonTypeModal(false);
    setShowVideoLessonForm(true);
  };

  const handleAddQuizLesson = () => {
    setShowLessonTypeModal(false);
    setShowQuizForm(true);
    // Reset quiz state
    setQuizTitle('');
    setQuestions([]);
    setCurrentQuestion({
      id: 1,
      text: '',
      options: ['', '', '', ''],
      correctOptionIndex: null,
      type: 'MultipleChoice'
    });
  };

  const handleAddFileLesson = () => {
    setShowLessonTypeModal(false);
    setShowFileLessonForm(true);
    // Reset file lesson state
    setFileLessonTitle('');
    setLessonFiles([]);
    setNewFileName('');
    setNewFileUrl('');
  };

  const handleCreateVideoLesson = async () => {
    if (!videoLessonTitle || !videoUrl) {
      setSaveMessage({
        type: 'error',
        text: 'يرجى ملء جميع الحقول المطلوبة'
      });
      return;
    }

    try {
      // Get the unit ID from the selected unit index
      const selectedUnit = units[selectedUnitIndex!];
      if (!selectedUnit) {
        throw new Error('No unit selected');
      }

      // Calculate the order (last order + 1)
      const lastOrder = selectedUnit.lessons.length > 0 
        ? Math.max(...selectedUnit.lessons.map(l => l.order || 0)) 
        : 0;

      const response = await authFetch(apiUrl('Lesson/add'), {
        method: 'POST',
        headers: {
          'accept': '*/*',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          lessonName: videoLessonTitle,
          titel: "null",
          unitId: selectedUnit.id,
          active: true,
          order: lastOrder + 1,
          type: "video",
          videoUrl: videoUrl,
        })
      });

      const data = await response.json();

      if (data.success) {
        // Show success message
        setSaveMessage({
          type: 'success',
          text: 'تم إضافة الدرس بنجاح'
        });

        // Refresh the course data to get the updated lessons
        const courseResponse = await authFetch(apiUrl(`Course/tree?courseid=${courseId}`), {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });
        
        const courseData = await courseResponse.json();
        
        if (courseData.success) {
          // Update the units state with the new data
          if (courseData.data && courseData.data.units) {
            setUnits(courseData.data.units.map((unit: any) => ({
              id: unit.id,
              title: unit.unitName || unit.titel,
              lessons: unit.lessons.map((lesson: any) => ({
                id: lesson.id,
                title: lesson.lessonName || lesson.titel,
                type: lesson.type === 'Video' ? 'video' 
                  : lesson.type === 'File' ? 'file' 
                  : lesson.type === 'Assignment' ? 'assignment'
                  : 'quiz',
                isVisible: lesson.active,
                order: lesson.order
              })),
              isVisible: unit.active
            })));
          }
        }

        // Clear the form and close the modal
    setVideoLessonTitle('');
    setVideoUrl('');
    setAttachmentTitle('');
    setAttachmentUrl('');
    setShowVideoLessonForm(false);

        // Clear the success message after 3 seconds
        setTimeout(() => {
          setSaveMessage(null);
        }, 3000);
      } else {
        setSaveMessage({
          type: 'error',
          text: data.message || 'فشل في إضافة الدرس'
        });
      }
    } catch (error) {
      console.error('Error adding lesson:', error);
      setSaveMessage({
        type: 'error',
        text: 'فشل في إضافة الدرس. يرجى المحاولة مرة أخرى لاحقًا.'
      });
    }
  };

  const handleQuestionChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setCurrentQuestion({
      ...currentQuestion,
      text: e.target.value
    });
  };

  const handleOptionChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const newOptions = [...currentQuestion.options];
    newOptions[index] = e.target.value;
    setCurrentQuestion({
      ...currentQuestion,
      options: newOptions
    });
  };

  const handleCorrectAnswerSelect = (index: number) => {
    setCurrentQuestion({
      ...currentQuestion,
      correctOptionIndex: index
    });
  };

  const handleCreateQuiz = async () => {
    if (!quizTitle.trim()) {
      setSaveMessage({
        type: 'error',
        text: 'يرجى إدخال عنوان الاختبار'
      });
      return;
    }

    if (questions.length === 0) {
      setSaveMessage({
        type: 'error',
        text: 'يرجى إضافة سؤال واحد على الأقل'
      });
      return;
    }

    try {
      // Get the unit ID from the selected unit index
      const selectedUnit = units[selectedUnitIndex!];
      if (!selectedUnit) {
        throw new Error('No unit selected');
      }

      // Calculate the order (last order + 1)
      const lastOrder = selectedUnit.lessons.length > 0 
        ? Math.max(...selectedUnit.lessons.map(l => l.order || 0)) 
        : 0;

      // API requires exactly 4 answers per multiple choice question
      const normalizeAnswers = (answers: { text: string; isCorrect: boolean }[], _type: string) => {
        const list = answers.slice(0, 4);
        while (list.length < 4) {
          list.push({ text: '', isCorrect: false });
        }
        return list;
      };

      const response = await authFetch(apiUrl('Lesson/add'), {
        method: 'POST',
        headers: {
          'accept': '*/*',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          lessonName: quizTitle,
          titel: quizTitle,
          unitId: selectedUnit.id,
          active: true,
          order: lastOrder + 1,
          type: "quiz",
          questions: questions.map(question => {
            const answers = question.options.map((option, index) => ({
              text: option,
              isCorrect: index === question.correctOptionIndex
            }));
            return {
              text: question.text,
              type: question.type,
              answers: question.type === 'MultipleChoice' ? normalizeAnswers(answers, question.type) : answers
            };
          })
        })
      });

      const data = await response.json();

      if (data.success) {
        // Show success message
        setSaveMessage({
          type: 'success',
          text: 'تم إضافة الاختبار بنجاح'
        });

        // Refresh the course data to get the updated lessons
        const courseResponse = await authFetch(apiUrl(`Course/tree?courseid=${courseId}`), {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });
        
        const courseData = await courseResponse.json();
        
        if (courseData.success) {
          // Update the units state with the new data
          if (courseData.data && courseData.data.units) {
            setUnits(courseData.data.units.map((unit: any) => ({
              id: unit.id,
              title: unit.unitName || unit.titel,
              lessons: unit.lessons.map((lesson: any) => ({
                id: lesson.id,
                title: lesson.lessonName || lesson.titel,
                type: lesson.type === 'Video' ? 'video' 
                  : lesson.type === 'File' ? 'file' 
                  : lesson.type === 'Assignment' ? 'assignment'
                  : 'quiz',
                isVisible: lesson.active,
                order: lesson.order
              })),
              isVisible: unit.active
            })));
          }
        }

        // Clear the form and close the modal
    setQuizTitle('');
        setQuestions([]);
        setCurrentQuestion({
          id: 1,
          text: '',
          options: ['', '', '', ''],
          correctOptionIndex: null,
          type: 'MultipleChoice'
        });
        setShowQuizForm(false);

        // Clear the success message after 3 seconds
        setTimeout(() => {
          setSaveMessage(null);
        }, 3000);
      } else {
        setSaveMessage({
          type: 'error',
          text: data.message || 'فشل في إضافة الاختبار'
        });
      }
    } catch (error) {
      console.error('Error adding quiz:', error);
      setSaveMessage({
        type: 'error',
        text: 'فشل في إضافة الاختبار. يرجى المحاولة مرة أخرى لاحقًا.'
      });
    }
  };

  // Fetch list of courses for importing quiz questions when quiz form opens
  useEffect(() => {
    if (!showQuizForm) return;

    const fetchImportCourses = async () => {
      setImportCoursesLoading(true);
      try {
        const response = await authFetch(
          apiUrl('Course/all?pageNumber=1&pageSize=100'),
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authService.getToken()}`
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch courses for import');
        }

        const data = await response.json();

        if (data.success && data.data && Array.isArray(data.data.data)) {
          const courseData = data.data;
          const formatted: ImportCourseSummary[] = courseData.data.map((course: any) => ({
            id: course.id,
            name: course.courseName
          }));
          setImportCourses(formatted);
        } else {
          setImportCourses([]);
        }
      } catch (error) {
        console.error('Error fetching import courses:', error);
        setImportCourses([]);
      } finally {
        setImportCoursesLoading(false);
      }
    };

    fetchImportCourses();
  }, [showQuizForm]);

  const fetchImportUnitsForCourse = async (courseIdForImport: string) => {
    setImportUnits([]);
    setSelectedImportUnitId(null);
    setImportQuizzes([]);
    setSelectedImportQuizId(null);

    if (!courseIdForImport) return;

    try {
      const response = await authFetch(apiUrl(`Course/tree?courseid=${courseIdForImport}`), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });

      const data = await response.json();

      if (data.success && data.data && Array.isArray(data.data.units)) {
        const mappedUnits: Unit[] = data.data.units.map((unit: any) => ({
          id: unit.id,
          title: unit.unitName || unit.titel || unit.title,
          lessons: (unit.lessons || []).map((lesson: any) => ({
            id: lesson.id,
            title: lesson.lessonName || lesson.titel || lesson.title,
            type:
              lesson.type === 'Video'
                ? 'video'
                : lesson.type === 'File'
                ? 'file'
                : lesson.type === 'Assignment'
                ? 'assignment'
                : 'quiz',
            isVisible: lesson.active,
            order: lesson.order
          })),
          isVisible: unit.active
        }));
        setImportUnits(mappedUnits);
      } else {
        setImportUnits([]);
      }
    } catch (error) {
      console.error('Error fetching units for import course:', error);
      setImportUnits([]);
    }
  };

  // Update list of available quizzes in selected import unit
  useEffect(() => {
    if (!selectedImportUnitId) {
      setImportQuizzes([]);
      setSelectedImportQuizId(null);
      return;
    }

    const unit = importUnits.find(u => u.id === selectedImportUnitId);
    if (unit && unit.lessons) {
      setImportQuizzes(unit.lessons.filter(lesson => lesson.type === 'quiz'));
    } else {
      setImportQuizzes([]);
    }
    setSelectedImportQuizId(null);
  }, [selectedImportUnitId, importUnits]);

  // Import quiz questions from an existing lesson into the quiz creation form
  const handleImportQuizIntoForm = async () => {
    if (!selectedImportCourseId || !selectedImportUnitId || !selectedImportQuizId) {
      setSaveMessage({
        type: 'error',
        text: 'يرجى اختيار الدورة والوحدة والاختبار المراد استيراده'
      });
      return;
    }

    setImportingQuiz(true);

    try {
      const lessonResponse = await authFetch(apiUrl(`Lesson/${selectedImportQuizId}`), {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });

      if (!lessonResponse.ok) {
        throw new Error(`HTTP error! status: ${lessonResponse.status}`);
      }

      const responseText = await lessonResponse.text();
      let lessonResult: any;
      try {
        lessonResult = JSON.parse(responseText);
      } catch (e) {
        console.error('JSON parse error while importing quiz lesson into form:', e);
        throw new Error('استجابة غير صالحة من الخادم عند جلب بيانات الاختبار');
      }

      if (!lessonResult.success || !lessonResult.data) {
        throw new Error(lessonResult.message || 'فشل في جلب بيانات الاختبار المحدد');
      }

      const sourceLesson = lessonResult.data;
      const sourceQuestions = Array.isArray(sourceLesson.questions) ? sourceLesson.questions : [];

      if (sourceQuestions.length === 0) {
        setSaveMessage({
          type: 'error',
          text: 'الاختبار المحدد لا يحتوي على أسئلة لاستيرادها'
        });
        return;
      }

      const existingMaxId = questions.length > 0 ? Math.max(...questions.map(q => q.id)) : 0;

      const importedQuestions: Question[] = sourceQuestions.map((q: any, index: number) => {
        const answers = Array.isArray(q.answers) ? q.answers : [];
        const type = mapImportedQuestionType(q, answers);
        const usableAnswers =
          type === 'TrueFalse'
            ? answers.filter((a: any) => (a?.text ?? '').toString().trim() !== '').slice(0, 2)
            : answers;
        const options = usableAnswers.map((a: any) => a.text);
        const correctIndex = usableAnswers.findIndex((a: any) => a.isCorrect);

        return {
          id: existingMaxId + index + 1,
          text: q.text,
          options: type === 'TrueFalse' && options.length === 0 ? ['True', 'False'] : options,
          correctOptionIndex: correctIndex >= 0 ? correctIndex : null,
          type
        };
      });

      setQuestions(importedQuestions);

      setSaveMessage({
        type: 'success',
        text: 'تم استيراد أسئلة الاختبار بنجاح. لا تنس الضغط على "إنشاء اختبار" لحفظه.'
      });
    } catch (error) {
      console.error('Error importing quiz into form:', error);
      setSaveMessage({
        type: 'error',
        text:
          error instanceof Error
            ? error.message
            : 'حدث خطأ أثناء استيراد أسئلة الاختبار. يرجى المحاولة مرة أخرى.'
      });
    } finally {
      setImportingQuiz(false);
    }
  };

  const handleDeleteQuestion = (questionId: number) => {
    setQuestions(questions.filter(q => q.id !== questionId));
  };

  const handleCreateFileLesson = async () => {
    if (!fileLessonTitle.trim()) {
      setSaveMessage({
        type: 'error',
        text: 'يرجى إدخال عنوان الدرس'
      });
      return;
    }

    // Build the files array - include current file inputs if filled
    const allFiles: LessonFile[] = [...lessonFiles];
    
    // If current file inputs are filled, add them as the first file (or next file)
    if (newFileName.trim() && newFileUrl.trim()) {
      allFiles.push({
        fileName: newFileName.trim(),
        fileUrl: newFileUrl.trim(),
        order: allFiles.length + 1
      });
    }

    if (allFiles.length === 0) {
      setSaveMessage({
        type: 'error',
        text: 'يرجى إضافة ملف PDF واحد على الأقل'
      });
      return;
    }

    try {
      // Get the unit ID from the selected unit index
      const selectedUnit = units[selectedUnitIndex!];
      if (!selectedUnit) {
        throw new Error('No unit selected');
      }

      // Calculate the order (last order + 1)
      const lastOrder = selectedUnit.lessons.length > 0 
        ? Math.max(...selectedUnit.lessons.map(l => l.order || 0)) 
        : 0;

      const response = await authFetch(apiUrl('Lesson/add'), {
        method: 'POST',
        headers: {
          'accept': '*/*',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          lessonName: fileLessonTitle,
          titel: fileLessonTitle,
          unitId: selectedUnit.id,
          active: true,
          order: lastOrder + 1,
          type: "File",
          files: allFiles.map(file => ({
            fileName: file.fileName,
            fileUrl: file.fileUrl,
            order: file.order
          }))
        })
      });

      const data = await response.json();

      if (data.success) {
        // Show success message
        setSaveMessage({
          type: 'success',
          text: 'تم إضافة درس PDF بنجاح'
        });

        // Refresh the course data to get the updated lessons
        const courseResponse = await authFetch(apiUrl(`Course/tree?courseid=${courseId}`), {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });
        
        const courseData = await courseResponse.json();
        
        if (courseData.success) {
          // Update the units state with the new data
          if (courseData.data && courseData.data.units) {
            setUnits(courseData.data.units.map((unit: any) => ({
              id: unit.id,
              title: unit.unitName || unit.titel,
              lessons: unit.lessons.map((lesson: any) => ({
                id: lesson.id,
                title: lesson.lessonName || lesson.titel,
                type: lesson.type === 'Video' ? 'video' 
                  : lesson.type === 'File' ? 'file' 
                  : lesson.type === 'Assignment' ? 'assignment'
                  : 'quiz',
                isVisible: lesson.active,
                order: lesson.order
              })),
              isVisible: unit.active
            })));
          }
        }

        // Clear the form and close the modal
        setFileLessonTitle('');
        setLessonFiles([]);
        setNewFileName('');
        setNewFileUrl('');
        setShowFileLessonForm(false);

        // Clear the success message after 3 seconds
        setTimeout(() => {
          setSaveMessage(null);
        }, 3000);
      } else {
        setSaveMessage({
          type: 'error',
          text: data.message || 'فشل في إضافة درس PDF'
        });
      }
    } catch (error) {
      console.error('Error adding file lesson:', error);
      setSaveMessage({
        type: 'error',
        text: 'فشل في إضافة درس PDF. يرجى المحاولة مرة أخرى لاحقًا.'
      });
    }
  };

  const handleAddFile = () => {
    if (!newFileName.trim() || !newFileUrl.trim()) {
      setSaveMessage({
        type: 'error',
        text: 'يرجى ملء اسم ملف PDF ورابط الملف'
      });
      return;
    }

    const newFile: LessonFile = {
      fileName: newFileName,
      fileUrl: newFileUrl,
      order: lessonFiles.length + 1
    };

    setLessonFiles([...lessonFiles, newFile]);
    setNewFileName('');
    setNewFileUrl('');
    
    // Show success message (brief, as it's just for adding to the list)
    // The main success message will be shown when the lesson is created
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = lessonFiles.filter((_, i) => i !== index).map((file, i) => ({
      ...file,
      order: i + 1
    }));
    setLessonFiles(updatedFiles);
  };

  const handleLessonClick = (e: MouseEventType, lesson: Lesson, unitId?: number) => {
    e.stopPropagation();
    
    // If it's an assignment, navigate to Assignment route
    if (lesson.type === 'assignment') {
      if (unitId) {
        navigate(`/assignment/${courseId}/${unitId}?lessonId=${lesson.id}`);
      } else {
        navigate(`/assignment/${courseId}?lessonId=${lesson.id}`);
      }
    } else {
      // For other lesson types, navigate to lesson view
      navigate(`/courses/${courseId}/lessons/${lesson.id}`, {
        state: { from: 'course' }
      });
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (course) {
      // Special handling for different field types
      if (name === 'price') {
        setCourse({
          ...course,
          price: parseFloat(value) || 0
        });
      } else if (name === 'active') {
        setCourse({
          ...course,
          active: value === 'active'
        });
      } else if (name === 'isOpenToAll') {
        setCourse({
          ...course,
          isOpenToAll: value === 'true'
        });
      } else if (name === 'isSuggested') {
        setCourse({
          ...course,
          isSuggested: value === 'true'
        });
      } else {
        setCourse({
          ...course,
          [name]: value
        });
      }
    }
  };

  // Fix input handlers
  const handleTextChange = (setter: (value: string) => void) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setter(e.target.value);
  };

  const handleNumberChange = (setter: (value: number) => void) => (e: ChangeEvent<HTMLInputElement>) => {
    setter(parseFloat(e.target.value) || 0);
  };

  const handleSelectChange = (setter: (value: string) => void) => (e: ChangeEvent<HTMLSelectElement>) => {
    setter(e.target.value);
  };

  const handleAddAttachment = () => {
    if (!newAttachment.title || !newAttachment.url) {
                      alert('يرجى ملء عنوان المرفق والرابط');
      return;
    }
    setAttachments([...attachments, { ...newAttachment, id: Date.now() }]);
    setNewAttachment({ id: Date.now() + 1, title: '', url: '' });
  };

  const handleRemoveAttachment = (id: number) => {
    setAttachments(attachments.filter(att => att.id !== id));
  };

  // Update event handlers with proper types
  const handleVideoUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => setVideoUrl(e.target.value);
  const handleAttachmentTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => setAttachmentTitle(e.target.value);
  const handleAttachmentUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => setAttachmentUrl(e.target.value);
  const handleQuizTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => setQuizTitle(e.target.value);

  // Add question edit functionality
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  
  const handleEditQuestion = (question: Question) => {
    setEditingQuestion(question);
    setCurrentQuestion({ ...question });
    setShowQuestionForm(true);
  };

  const handleUpdateQuestion = () => {
    if (!currentQuestion.text.trim()) {
      alert('Please enter a question');
      return;
    }

    if (currentQuestion.options.some(option => !option.trim())) {
      alert('Please fill all options');
      return;
    }

    if (currentQuestion.correctOptionIndex === null) {
      alert('Please select the correct answer');
      return;
    }

    if (editingQuestion) {
      // Update existing question
      setQuestions(questions.map(q => 
        q.id === editingQuestion.id ? currentQuestion : q
      ));
    } else {
      // Add new question
      setQuestions([...questions, currentQuestion]);
    }

    setCurrentQuestion({
      id: editingQuestion ? editingQuestion.id + 1 : currentQuestion.id + 1,
      text: '',
      options: ['', '', '', ''],
      correctOptionIndex: null,
      type: 'MultipleChoice'
    });
    
    setEditingQuestion(null);
    setShowQuestionForm(false);
  };

  const [showQuestionForm, setShowQuestionForm] = useState(false);

  // Add function to save course details
  const handleSaveCourse = async () => {
    if (!course) return;
    
    // // Validate price
    // if (course.price <= 0) {
    //   setSaveMessage({
    //     type: 'error',
    //     text: 'يجب أن يكون السعر أكبر من صفر'
    //   });
    //   return;
    // }

    // Validate image if one is selected
    if (imageFile) {
      if (!ALLOWED_TYPES.includes(imageFile.type)) {
        setSaveMessage({
          type: 'error',
          text: 'الرجاء اختيار ملف صورة صالح (JPEG، PNG، أو GIF)'
        });
        return;
      }

      if (imageFile.size > MAX_FILE_SIZE) {
        setSaveMessage({
          type: 'error',
          text: 'يجب أن يكون حجم الصورة أقل من 5 ميجابايت'
        });
        return;
      }
    }
    
    setIsSaving(true);
    setSaveMessage(null);
    
    try {
      const formData = new FormData();
      formData.append('CourseName', course.courseName);
      formData.append('SubjectId', course.subjectId);
      
      // Append TeacherIds array if available, otherwise use teacherId for backward compatibility
      const teacherIds = course.teacherIds || (course.teacherId ? [course.teacherId] : []);
      if (teacherIds.length > 0) {
        teacherIds.forEach((teacherId, index) => {
          formData.append(`TeacherIds[${index}]`, teacherId);
        });
        // Also append TeacherId for backward compatibility (first teacher as primary)
        formData.append('TeacherId', teacherIds[0]);
      } else if (course.teacherId) {
        formData.append('TeacherId', course.teacherId);
      }
      
      formData.append('Term', course.term);
      formData.append('Description', course.description || '');
      formData.append('Active', course.active.toString());
      formData.append('Price', course.price.toString());
      formData.append('IsOpenToAll', course.isOpenToAll.toString());
      formData.append('IsSuggested', course.isSuggested.toString());
      
      // Handle image upload
      if (imageFile) {
        // Create a new blob with the correct type to ensure proper upload
        const blob = new Blob([imageFile], { type: imageFile.type });
        formData.append('ImageFile', blob, imageFile.name);
      }

      const response = await authFetch(apiUrl(`Course/${courseId}/updateWithImage`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSaveMessage({
          type: 'success',
          text: 'تم تحديث الدورة بنجاح'
        });
        
        // Refresh course data to get updated teachers from API
        if (courseId) {
          const refreshResponse = await authFetch(apiUrl(`Course/${courseId}`), {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authService.getToken()}`
            }
          });
          
          const refreshData = await refreshResponse.json();
          if (refreshData.success) {
            setCourse(refreshData.data);
          }
        }
        
        // Update sessionStorage with the latest data
        sessionStorage.setItem('currentCourseData', JSON.stringify(course));
      } else {
        setSaveMessage({
          type: 'error',
          text: data.message || 'فشل في تحديث الدورة'
        });
      }
    } catch (error) {
      console.error('Error saving course:', error);
      setSaveMessage({
        type: 'error',
        text: 'فشل في تحديث الدورة. يرجى المحاولة مرة أخرى لاحقًا.'
      });
    } finally {
      setIsSaving(false);
      
      // Clear the message after 3 seconds
      setTimeout(() => {
        setSaveMessage(null);
      }, 3000);
    }
  };

  const handleDeleteUnitConfirm = (e: MouseEventType, unitId: number, index: number) => {
    e.stopPropagation();
    setUnitToAction({ id: unitId, index });
    setShowDeleteUnitModal(true);
  };

  const confirmDeleteUnit = async () => {
    if (unitToAction) {
      // Close the modal immediately when user clicks confirm
      setShowDeleteUnitModal(false);
      setUnitToAction(null);
      
      try {
        setIsSaving(true);
        
        // Call API to delete the unit
        const response = await authFetch(apiUrl(`Unit/${unitToAction.id}`), {
          method: 'DELETE',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });
        
        const data = await response.json();
        
        if (data.success) {
          // Update the local state only after successful API call
          setUnits(units.filter(unit => unit.id !== unitToAction.id));
          setSaveMessage({
            type: 'success',
            text: 'تم حذف المحاضرة بنجاح'
          });
        } else {
          setSaveMessage({
            type: 'error',
            text: data.message || 'فشل في حذف المحاضرة'
          });
        }
      } catch (error) {
        console.error('Error deleting unit:', error);
        setSaveMessage({
          type: 'error',
          text: 'فشل في حذف المحاضرة. يرجى المحاولة مرة أخرى لاحقًا.'
        });
      } finally {
        setIsSaving(false);
        
        // Clear the success message after 3 seconds
        setTimeout(() => {
          setSaveMessage(null);
        }, 3000);
      }
    }
  };

  const handleHideUnitConfirm = (e: MouseEventType, unitId: number, index: number) => {
    e.stopPropagation();
    setUnitToAction({ id: unitId, index });
    setShowHideUnitModal(true);
  };

  const confirmHideUnit = async () => {
    if (unitToAction) {
      // Close the modal immediately when user clicks confirm
      setShowHideUnitModal(false);
      setUnitToAction(null);
      
      try {
        setIsSaving(true);
        const updatedUnits = [...units];
        const unitIndex = updatedUnits.findIndex(unit => unit.id === unitToAction.id);
        
        if (unitIndex !== -1) {
          // Toggle visibility in local state
          const newVisibility = !updatedUnits[unitIndex].isVisible;
          updatedUnits[unitIndex].isVisible = newVisibility;
          
          // Call API to update unit visibility
          const response = await authFetch(apiUrl(`Unit/${unitToAction.id}`), {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'accept': '*/*',
              'Authorization': `Bearer ${authService.getToken()}`
            },
            body: JSON.stringify({
              unitName: updatedUnits[unitIndex].title,
              titel: "null",
              courseId: courseId,
              active: newVisibility
            })
          });
          
          const data = await response.json();
          
          if (data.success) {
            setUnits(updatedUnits);
            setSaveMessage({
              type: 'success',
              text: `تم ${newVisibility ? 'إظهار' : 'إخفاء'} المحاضرة بنجاح`
            });
          } else {
            // Revert local change if API fails
            updatedUnits[unitIndex].isVisible = !newVisibility;
            setUnits(updatedUnits);
            setSaveMessage({
              type: 'error',
              text: data.message || 'فشل في تحديث رؤية المحاضرة'
            });
          }
        }
      } catch (error) {
        console.error('Error updating unit visibility:', error);
        setSaveMessage({
          type: 'error',
          text: 'فشل في تحديث رؤية المحاضرة. يرجى المحاولة مرة أخرى لاحقًا.'
        });
      } finally {
        setIsSaving(false);
        
        // Clear the success message after 3 seconds
        setTimeout(() => {
          setSaveMessage(null);
        }, 3000);
      }
    }
  };

  const handleDeleteLessonConfirm = (unitIndex: number, lessonId: number) => {
    setLessonToAction({ unitIndex, lessonId });
    setShowDeleteLessonModal(true);
  };

  const confirmDeleteLesson = async () => {
    if (lessonToAction) {
      // Close the modal immediately when user clicks confirm
      setShowDeleteLessonModal(false);
      setLessonToAction(null);
      
      try {
        setIsSaving(true);
        
        // Get the lesson ID
        const lessonId = lessonToAction.lessonId;
        
        // Call API to delete the lesson
        const response = await authFetch(apiUrl(`Lesson/${lessonId}`), {
          method: 'DELETE',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });
        
        const data = await response.json();
        
        if (data.success) {
          // Update the local state only after successful API call
          const updatedUnits = [...units];
          updatedUnits[lessonToAction.unitIndex].lessons = updatedUnits[lessonToAction.unitIndex].lessons.filter(
            lesson => lesson.id !== lessonToAction.lessonId
          );
          setUnits(updatedUnits);
          setSaveMessage({
            type: 'success',
            text: 'تم حذف الدرس بنجاح'
          });
        } else {
          setSaveMessage({
            type: 'error',
            text: data.message || 'فشل في حذف الدرس'
          });
        }
      } catch (error) {
        console.error('Error deleting lesson:', error);
        setSaveMessage({
          type: 'error',
          text: 'فشل في حذف الدرس. يرجى المحاولة مرة أخرى لاحقًا.'
        });
      } finally {
        setIsSaving(false);
        
        // Clear the success message after 3 seconds
        setTimeout(() => {
          setSaveMessage(null);
        }, 3000);
      }
    }
  };

  const handleHideLessonConfirm = (unitIndex: number, lessonId: number) => {
    setLessonToAction({ unitIndex, lessonId });
    setShowHideLessonModal(true);
  };

  const confirmHideLesson = async () => {
    if (lessonToAction) {
      // Close the modal immediately when user clicks confirm
      setShowHideLessonModal(false);
      setLessonToAction(null);
      
      try {
        setIsSaving(true);
        const updatedUnits = [...units];
        const unitIndex = lessonToAction.unitIndex;
        const lessonIndex = updatedUnits[unitIndex].lessons.findIndex(
          lesson => lesson.id === lessonToAction.lessonId
        );
        
        if (lessonIndex !== -1) {
          // Toggle visibility in local state
          const newVisibility = !updatedUnits[unitIndex].lessons[lessonIndex].isVisible;
          updatedUnits[unitIndex].lessons[lessonIndex].isVisible = newVisibility;
          
          // Get the lessonId and unitId
          const lessonId = lessonToAction.lessonId;
          const unitId = updatedUnits[unitIndex].id;
          const lessonTitle = updatedUnits[unitIndex].lessons[lessonIndex].title;
          const lessonType = updatedUnits[unitIndex].lessons[lessonIndex].type;
          const lessonOrder = updatedUnits[unitIndex].lessons[lessonIndex].order || 0;
          
          // First, fetch the current lesson details to get all existing properties
          const lessonDetailsResponse = await authFetch(apiUrl(`Lesson/${lessonId}`), {
            method: 'GET',
            headers: {
              'accept': '*/*',
              'Authorization': `Bearer ${authService.getToken()}`
            }
          });
          
          const lessonDetails = await lessonDetailsResponse.json();
          
          if (!lessonDetails.success) {
            throw new Error('Failed to fetch lesson details');
          }
          
          // Prepare the complete payload with existing properties and updated visibility
          const lessonPayload: any = {
            lessonName: lessonTitle,
            titel: lessonTitle, // Use the same title for both fields
            unitId: unitId,
            active: newVisibility, // This is the only property we're changing
            order: lessonOrder,
            type: lessonType === 'video' ? 'video' 
              : lessonType === 'file' ? 'File' 
              : lessonType === 'assignment' ? 'Assignment'
              : 'quiz',
            videoUrl: lessonDetails.data?.videoUrl || null,
            attachmentTitel: lessonDetails.data?.attachmentTitel || null,
            attachmentUrl: lessonDetails.data?.attachmentUrl || null,
            questions: lessonDetails.data?.questions || []
          };

          // Add files array for File type lessons
          if (lessonType === 'file') {
            lessonPayload.files = lessonDetails.data?.files || [];
          }
          
          // Call API to update lesson visibility
          const response = await authFetch(apiUrl(`Lesson/${lessonId}`), {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'accept': '*/*',
              'Authorization': `Bearer ${authService.getToken()}`
            },
            body: JSON.stringify(lessonPayload)
          });
          
          const data = await response.json();
          
          if (data.success) {
            setUnits(updatedUnits);
            setSaveMessage({
              type: 'success',
              text: `تم ${newVisibility ? 'إظهار' : 'إخفاء'} الدرس بنجاح`
            });
          } else {
            // Revert local change if API fails
            updatedUnits[unitIndex].lessons[lessonIndex].isVisible = !newVisibility;
            setUnits(updatedUnits);
            setSaveMessage({
              type: 'error',
              text: data.message || 'فشل في تحديث رؤية الدرس'
            });
          }
        }
      } catch (error) {
        console.error('Error updating lesson visibility:', error);
        setSaveMessage({
          type: 'error',
          text: 'فشل في تحديث رؤية الدرس. يرجى المحاولة مرة أخرى لاحقًا.'
        });
      } finally {
        setIsSaving(false);
        
        // Clear the success message after 3 seconds
        setTimeout(() => {
          setSaveMessage(null);
        }, 3000);
      }
    }
  };

  // Add function to fetch enrolled students with pagination and filters
  const fetchEnrolledStudents = useCallback(async (filterOverride?: { searchTerm?: string; name?: string }) => {
    if (!courseId) return;
    
    setLoadingStudents(true);
    setStudentsError(null);
    
    const filters = filterOverride ?? studentFilters;
    
    try {
      // Prepare URL with query parameters - Note: The current API doesn't support pagination
      let url = apiUrl(`AdminStudent/studentincourses/${courseId}`);
      
      // Add filters if they exist
      if (filters.searchTerm || filters.name) {
        // Add query parameter indicator
        url += '?';
        
        // Add filters
        const params = [];
        if (filters.searchTerm) {
          params.push(`searchTerm=${encodeURIComponent(filters.searchTerm)}`);
        }
        if (filters.name) {
          params.push(`name=${encodeURIComponent(filters.name)}`);
        }
        
        url += params.join('&');
      }
      
      const response = await authFetch(url, {
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch enrolled students');
      }
      
      const result = await response.json();
      
      if (result.success) {
        // The API returns an array directly in result.data
        const students = Array.isArray(result.data) ? result.data : [];
        setEnrolledStudents(students);
        
        // Since API doesn't support pagination yet, we'll handle it client-side
        setStudentTotalCount(students.length);
        
        // Calculate total pages based on our page size
        const calculatedTotalPages = Math.max(1, Math.ceil(students.length / studentPageSize));
        setStudentTotalPages(calculatedTotalPages);
        
        // Adjust current page if it's now out of bounds
        if (studentCurrentPage > calculatedTotalPages) {
          setStudentCurrentPage(1);
        }
      } else {
        throw new Error(result.message || 'Failed to fetch enrolled students');
      }
    } catch (error) {
      console.error('Error fetching enrolled students:', error);
      setStudentsError('Failed to load enrolled students. Please try again later.');
      setEnrolledStudents([]);
      setStudentTotalCount(0);
      setStudentTotalPages(1);
    } finally {
      setLoadingStudents(false);
    }
  }, [courseId, studentFilters, studentCurrentPage, studentPageSize]);

  // Refetch students only when tab/course/page changes — search runs when user clicks Filter
  useEffect(() => {
    if (activeTab === 'students') {
      fetchEnrolledStudents();
    }
  }, [activeTab, courseId, studentCurrentPage, fetchEnrolledStudents]);
  
  // Add function to handle student page change
  const handleStudentPageChange = (newPage: number) => {
    setStudentCurrentPage(newPage);
  };
  
  // Add function to handle student filter changes
  const handleStudentFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setStudentFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Add function to apply student filters
  const applyStudentFilters = () => {
    setStudentCurrentPage(1); // Reset to first page when applying filters
    fetchEnrolledStudents();
  };
  
  // Add function to reset student filters
  const resetStudentFilters = () => {
    setStudentFilters({
      searchTerm: '',
      name: ''
    });
    setStudentCurrentPage(1);
    fetchEnrolledStudents({ searchTerm: '', name: '' });
  };
  
  // Add function to search for a student by mobile number
  const handleSearchStudent = async () => {
    if (!enrollStudentData.trim()) {
      setEnrollStudentError('Please enter a mobile number');
      return;
    }
    
    setSearchingStudent(true);
    setFoundStudent(null);
    setSearchSuccessMessage(null);
    setEnrollStudentError(null);
    
    try {
      // Call the search API
      const response = await authFetch(
        apiUrl(`AdminStudent/Studentsearch?PhoneNumber=${encodeURIComponent(enrollStudentData.trim())}`),
        {
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );
      
      const result = await response.json();
      
      if (result.success && result.data && result.data.students && result.data.students.length > 0) {
        // Student found
        setFoundStudent(result.data.students[0]);
        setSearchSuccessMessage(`Found student: ${result.data.students[0].userName}`);
      } else if (result.success && (!result.data.students || result.data.students.length === 0)) {
        setEnrollStudentError('No student found with this mobile number');
      } else {
        setEnrollStudentError(result.message || 'Failed to search for student');
      }
    } catch (error) {
      console.error('Error searching for student:', error);
      setEnrollStudentError('Failed to search for student. Please try again later.');
    } finally {
      setSearchingStudent(false);
    }
  };

  // Add function to enroll a student
  const handleEnrollStudent = async () => {
    if (!courseId || !foundStudent) {
      setEnrollStudentError('Please search for a student first');
      return;
    }
    
    setIsEnrollingStudent(true);
    setEnrollStudentError(null);
    
    try {
      // Call the enrollment API with the correct endpoint format
      const response = await authFetch(
        apiUrl(`AdminStudent/enroll/${foundStudent.id}/${courseId}`), 
        {
          method: 'PUT',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );
      
      const result = await response.json();
      
      if (result.success) {
        // Close the modal
        setShowEnrollStudentModal(false);
        // Reset the input
        setEnrollStudentData('');
        setFoundStudent(null);
        setSearchSuccessMessage(null);
        // Show success message
        setSaveMessage({
          type: 'success',
          text: 'تم تسجيل الطالب بنجاح'
        });
        // Refresh the student list
        fetchEnrolledStudents();
      } else {
        setEnrollStudentError(result.message || 'Failed to enroll student');
      }
    } catch (error) {
      console.error('Error enrolling student:', error);
      setEnrollStudentError('Failed to enroll student. Please try again later.');
    } finally {
      setIsEnrollingStudent(false);
    }
  };
  
  // Add function to remove a student
  const handleRemoveStudent = async () => {
    if (!courseId || !studentToRemove) return;
    
    setIsRemovingStudent(true);
    
    try {
      // Call the un-enroll API with correct endpoint
      const response = await authFetch(
        apiUrl(`AdminStudent/unenroll/${studentToRemove.id}/${courseId}`),
        {
          method: 'PUT',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );
      
      const result = await response.json();
      
      if (result.success) {
        // Close the modal
        setShowRemoveStudentModal(false);
        // Reset the student to remove
        setStudentToRemove(null);
        // Show success message
        setSaveMessage({
          type: 'success',
          text: result.data || 'Student removed from course successfully'
        });
        // Refresh the student list
        fetchEnrolledStudents();
      } else {
        setStudentsError('Failed to remove student from course: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error removing student:', error);
      setStudentsError('Failed to remove student from course. Please try again later.');
    } finally {
      setIsRemovingStudent(false);
    }
  };

  // Add function to handle edit unit icon click
  const handleEditUnitClick = (e: MouseEventType, unitId: number, index: number, currentName: string) => {
    e.stopPropagation();
    setUnitToAction({ id: unitId, index });
    setEditUnitName(currentName);
    setShowEditUnitModal(true);
  };
  
  // Add function to save edited unit name
  const confirmEditUnit = async () => {
    if (unitToAction && editUnitName.trim()) {
      try {
        setIsSaving(true);
        const updatedUnits = [...units];
        const unitIndex = updatedUnits.findIndex(unit => unit.id === unitToAction.id);
        
        if (unitIndex !== -1) {
          // Get the current unit visibility
          const isVisible = updatedUnits[unitIndex].isVisible;
          
          // Call API to update unit name
          const response = await authFetch(apiUrl(`Unit/${unitToAction.id}`), {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'accept': '*/*',
              'Authorization': `Bearer ${authService.getToken()}`
            },
            body: JSON.stringify({
              unitName: editUnitName,
              titel: "null",
              courseId: courseId,
              active: isVisible
            })
          });
          
          const data = await response.json();
          
          if (data.success) {
            // Update the unit name in the local state
            updatedUnits[unitIndex].title = editUnitName;
            setUnits(updatedUnits);
            setSaveMessage({
              type: 'success',
              text: 'تم إعادة تسمية المحاضرة بنجاح'
            });
          } else {
            setSaveMessage({
              type: 'error',
              text: data.message || 'فشل في إعادة تسمية المحاضرة'
            });
          }
        }
      } catch (error) {
        console.error('Error renaming unit:', error);
        setSaveMessage({
          type: 'error',
          text: 'فشل في إعادة تسمية المحاضرة. يرجى المحاولة مرة أخرى لاحقًا.'
        });
      } finally {
        setIsSaving(false);
        setShowEditUnitModal(false);
        setUnitToAction(null);
        
        // Clear the success message after 3 seconds
        setTimeout(() => {
          setSaveMessage(null);
        }, 3000);
      }
    }
  };

  // Add function to fetch quiz results
  const fetchQuizResults = async (lessonId: number) => {
    setLoadingQuizResults(true);
    setQuizResultsError(null);
    
    try {
      const response = await authFetch(apiUrl(`quizresult/lesson/${lessonId}`), {
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setQuizResults(data.data);
      } else {
        setQuizResultsError(data.message || 'Failed to fetch quiz results');
      }
    } catch (error) {
      console.error('Error fetching quiz results:', error);
      setQuizResultsError('Failed to fetch quiz results. Please try again later.');
    } finally {
      setLoadingQuizResults(false);
    }
  };

  // Add function to handle opening quiz results
  const handleViewQuizResults = (lessonId: number, e: MouseEventType) => {
    e.stopPropagation();
    setSelectedLessonId(lessonId);
    setQuizResultsSort({ score: false, date: false });
    fetchQuizResults(lessonId);
    setShowQuizResultsModal(true);
  };

  const toggleQuizResultsSort = (key: 'score' | 'date') => {
    setQuizResultsSort((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getSortedQuizResults = (): QuizResult[] => {
    if (!quizResults?.results) return [];
    const results = [...quizResults.results];
    const { score: sortByScore, date: sortByDate } = quizResultsSort;

    if (!sortByScore && !sortByDate) return results;

    return results.sort((a, b) => {
      if (sortByScore) {
        const scoreDiff = b.score - a.score;
        if (scoreDiff !== 0) return scoreDiff;
      }
      if (sortByDate) {
        return new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime();
      }
      return 0;
    });
  };

  // Add function to fetch student completion status
  const fetchStudentCompletion = async (lessonId: number) => {
    setLoadingStudentCompletion(true);
    setStudentCompletionError(null);
    
    try {
      const response = await authFetch(apiUrl(`Student/contentlessonCompletionStatus/${lessonId}`), {
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${authService.getToken()}`,
          'X-Tenant-ID': TENANT_ID
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setStudentCompletion(data.data);
      } else {
        setStudentCompletionError(data.message || 'Failed to fetch student completion status');
      }
    } catch (error) {
      console.error('Error fetching student completion status:', error);
      setStudentCompletionError('Failed to fetch student completion status. Please try again later.');
    } finally {
      setLoadingStudentCompletion(false);
    }
  };

  // Add function to handle opening student completion modal
  const handleViewStudentCompletion = (lessonId: number, e: MouseEventType) => {
    e.stopPropagation();
    setSelectedVideoLessonId(lessonId);
    fetchStudentCompletion(lessonId);
    setShowStudentCompletionModal(true);
  };

    return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Save/Error Message Toast */}
        {saveMessage && (
          <div className={`fixed top-4 left-4 px-6 py-3 rounded-lg shadow-lg z-50 flex items-center ${
            saveMessage.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
          }`}>
            {saveMessage.type === 'success' ? (
              <CheckIcon className="h-5 w-5 ml-2" />
            ) : (
              <XMarkIcon className="h-5 w-5 ml-2" />
            )}
            {saveMessage.text}
      </div>
        )}

        {/* Loading States */}
        {(loading || loadingCurriculum) && (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
        )}

        {/* Errors */}
        {(error || curriculumError) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
      </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error loading data</h3>
                <div className="mt-2 text-sm text-red-700">
                  {error && <p>{error}</p>}
                  {curriculumError && <p>{curriculumError}</p>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        {!loading && !loadingCurriculum && !error && !curriculumError && course && (
          <>
      <div className="flex items-center space-x-4 mb-6">
        <button
          onClick={() => navigate('/courses')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="w-5 h-5 ml-1" />
          العودة إلى الباقات
        </button>
        <div>
          <h1 className="text-3xl font-bold text-dark">{course.courseName}</h1>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex">
            <button
              onClick={() => setActiveTab('content')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'content'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              المحتوى
            </button>
            <button
              onClick={() => setActiveTab('details')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'details'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              تفاصيل الدورة
            </button>
            <button
              onClick={() => setActiveTab('students')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'students'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              الطلاب المسجلون
            </button>
          </nav>
        </div>

        {activeTab === 'content' && (
          <div className="p-6">
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-4">المنهج الدراسي</h2>
              
              <div className="space-y-4">
                {units.map((unit, unitIndex) => (
                  <div key={unit.id} className="border rounded-lg overflow-hidden">
                    <div 
                      className={`p-4 flex justify-between items-center cursor-pointer ${
                        unit.isVisible ? 'bg-gray-50' : 'bg-red-50'
                      }`}
                      onClick={() => toggleUnitExpand(unit.id)}
                    >
                      <div className="flex items-center">
                        {expandedUnits.includes(unit.id) ? (
                          <ChevronDownIcon className="w-5 h-5 ml-2 text-gray-600" />
                        ) : (
                          <ChevronRightIcon className="w-5 h-5 ml-2 text-gray-600" />
                        )}
                        <h3 className="font-medium">{unit.title}</h3>
                        {!unit.isVisible && (
                          <span className="text-xs text-red-500 mr-2">(مخفي)</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {canEditUnit && (
                        <button
                          onClick={(e: any) => handleEditUnitClick(e, unit.id, unitIndex, unit.title)}
                          className="text-blue-600 hover:text-blue-700"
                          title="تعديل المحاضرة"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        )}
                        {canEditUnit && (
                        <button
                          onClick={(e: any) => handleHideUnitConfirm(e, unit.id, unitIndex)}
                          className={`${
                            unit.isVisible ? 'text-gray-600' : 'text-red-600'
                          } hover:text-gray-900`}
                          title={unit.isVisible ? 'Hide Unit' : 'Show Unit'}
                        >
                          <EyeIcon className="w-5 h-5" />
                        </button>
                        )}
                        {canDeleteUnit && (
                        <button
                          onClick={(e: any) => handleDeleteUnitConfirm(e, unit.id, unitIndex)}
                          className="text-red-600 hover:text-red-700"
                          title="حذف المحاضرة"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                        )}
                      </div>
                    </div>
                    
                    {expandedUnits.includes(unit.id) && (
                      <div className="p-4 bg-white">
                        <ul className="space-y-2">
                          {unit.lessons.map((lesson) => (
                            <li key={lesson.id} className="p-2 border-b">
                              <div className="flex justify-between items-center">
                                <div 
                                  className="flex items-center gap-2 cursor-pointer"
                                  onClick={(e: any) => handleLessonClick(e, lesson, unit.id)}
                                >
                                  <span className={`text-xs px-2 py-1 rounded-full ${
                                    lesson.type === 'video' ? 'bg-blue-100 text-blue-800' 
                                      : lesson.type === 'file' ? 'bg-red-100 text-red-800'
                                      : lesson.type === 'assignment' ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-green-100 text-green-800'
                                  }`}>
                                    {lesson.type === 'video' ? 'فيديو' 
                                      : lesson.type === 'file' ? 'PDF'
                                      : lesson.type === 'assignment' ? 'واجب'
                                      : 'اختبار'}
                                  </span>
                                  <span>{lesson.title}</span>
                                  {!lesson.isVisible && (
                                    <span className="text-xs text-red-500 mr-2">(مخفي)</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  {lesson.type === 'quiz' && (
                                    <button
                                      onClick={(e: MouseEventType) => handleViewQuizResults(lesson.id, e)}
                                      className="text-indigo-600 hover:text-indigo-800"
                                      title="عرض نتائج الاختبار"
                                    >
                                      <DocumentTextIcon className="w-5 h-5" />
                                    </button>
                                  )}
                                  {lesson.type === 'video' && (
                                    <button
                                      onClick={(e: MouseEventType) => handleViewStudentCompletion(lesson.id, e)}
                                      className="text-blue-600 hover:text-blue-800"
                                      title="عرض حالة مشاهدة الطلاب"
                                    >
                                      <UserIcon className="w-5 h-5" />
                                    </button>
                                  )}
                                  {canEditLesson && (
                                  <button
                                    onClick={() => handleHideLessonConfirm(unitIndex, lesson.id)}
                                    className={`${
                                      lesson.isVisible ? 'text-gray-600' : 'text-red-600'
                                    } hover:text-gray-900`}
                                    title={lesson.isVisible ? 'Hide Lesson' : 'Show Lesson'}
                                  >
                                    <EyeIcon className="w-5 h-5" />
                                  </button>
                                  )}
                                  {canDeleteLesson && (
                                  <button
                                    onClick={() => handleDeleteLessonConfirm(unitIndex, lesson.id)}
                                    className="text-red-600 hover:text-red-700"
                                    title="حذف الدرس"
                                  >
                                    <TrashIcon className="w-5 h-5" />
                                  </button>
                                  )}
                                </div>
                              </div>
                            </li>
                          ))}
                          {canAddLesson && (
                          <li>
                            <button
                              onClick={() => handleAddLesson(unitIndex)}
                              className="w-full p-2 text-left text-primary hover:bg-primary/5 rounded flex items-center gap-2"
                            >
                              <PlusIcon className="w-5 h-5" />
                              <span>إضافة درس</span>
                            </button>
                          </li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {canAddUnit && (
              <button
                onClick={() => setShowAddUnitModal(true)}
                className="mt-4 flex items-center gap-2 text-primary hover:text-primary/80"
              >
                <PlusIcon className="w-5 h-5" />
                <span>إضافة وحدة</span>
              </button>
              )}
            </div>
          </div>
        )}

        {activeTab === 'details' && (
          <div className="p-6">
            {loading && (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            )}
            {!loading && !course && (
              <div className="text-center text-gray-500 py-8">
                <p>لا يمكن تحميل بيانات الدورة</p>
              </div>
            )}
            {!loading && course && (
              <>
                {/* Save message alert */}
            {saveMessage && (
              <div className={`mb-4 p-3 rounded ${
                saveMessage.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
              }`}>
                <div className="flex items-center">
                  {saveMessage.type === 'success' ? (
                    <CheckIcon className="h-5 w-5 mr-2" />
                  ) : (
                    <XMarkIcon className="h-5 w-5 mr-2" />
                  )}
                  {saveMessage.text}
                </div>
              </div>
            )}
            
            <form onSubmit={(e: FormEvent<HTMLFormElement>) => {
              e.preventDefault();
              // Handle form submission
            }} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">اسم الدورة</label>
                <input
                  type="text"
                  value={course?.courseName || ''}
                  onChange={handleInputChange}
                  name="courseName"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">المادة</label>
                  <div className="relative">
                    <select
                      value={course?.subjectId || ''}
                      onChange={handleSelectChange((value) => {
                        if (course) {
                          const selectedSubject = subjects.find(s => s.id === value);
                          setCourse({
                            ...course,
                            subjectId: value,
                            subjectName: selectedSubject?.name || '',
                            subjectCode: selectedSubject?.code || '',
                            gradeName: selectedSubject?.gradeName || ''
                          });
                        }
                      })}
                      name="subjectId"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm disabled:bg-gray-100"
                      disabled={loadingSubjects}
                    >
                      <option value="">اختر المادة</option>
                      {subjects.length > 0 ? (
                        subjects.map(subject => (
                          <option key={subject.id} value={subject.id}>
                            {subject.name}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>لا توجد مواد متاحة</option>
                      )}
                    </select>
                    {loadingSubjects && (
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent"></div>
                      </div>
                    )}
                  </div>
                  {subjectsError && (
                    <p className="mt-1 text-sm text-red-600">{subjectsError}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">المدرسون</label>
                  <div className="mt-1 space-y-2">
                    {course.teachers && course.teachers.length > 0 ? (
                      course.teachers.map((teacher) => (
                        <div key={teacher.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-900">{teacher.name}</span>
                            {teacher.isPrimary && (
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">Primary</span>
                            )}
                            <span className="text-xs text-gray-500">({teacher.email})</span>
                          </div>
                          {course.teachers && course.teachers.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveTeacher(teacher.id)}
                              className="text-red-600 hover:text-red-800 text-sm"
                              title="إزالة المعلم"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-gray-500 p-2 bg-gray-50 rounded-lg">
                        {course.teacherName || 'غير محدد'}
                      </div>
                    )}
                  </div>
                  
                  {/* Add Teacher Section */}
                  {course.teachers && course.teachers.length < 2 && (
                    <div className="mt-3 flex gap-2">
                      <select
                        value={selectedTeacherToAdd}
                        onChange={(e) => setSelectedTeacherToAdd(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        disabled={loadingTeachers}
                      >
                        <option value="">اختر معلم لإضافته</option>
                        {availableTeachers
                          .filter(teacher => {
                            const currentTeacherIds = course.teacherIds || (course.teacherId ? [course.teacherId] : []);
                            return !currentTeacherIds.includes(teacher.id);
                          })
                          .map((teacher) => (
                            <option key={teacher.id} value={teacher.id}>
                              {teacher.fullName}
                            </option>
                          ))}
                      </select>
                      <button
                        type="button"
                        onClick={handleAddTeacher}
                        disabled={!selectedTeacherToAdd || loadingTeachers}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      >
                        إضافة
                      </button>
                    </div>
                  )}
                  
                  <p className="mt-1 text-xs text-gray-500">
                    {course.teachers && course.teachers.length >= 2 
                      ? 'تم تعيين معلمين للدورة (الحد الأقصى)' 
                      : 'يمكن تعيين معلمين لكل دورة'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">الفصل</label>
                  <select
                    value={course?.term || ''}
                    onChange={handleSelectChange((value) => course && setCourse({ ...course, term: value }))}
                    name="term"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  >
                    <option value="">اختر الفصل</option>
                    <option value="First">الفصل الأول</option>
                    <option value="Second">الفصل الثاني</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">الحالة</label>
                  <select
                    value={course?.active ? 'active' : 'inactive'}
                    onChange={handleInputChange}
                    name="active"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  >
                    <option value="active">نشط</option>
                    <option value="inactive">غير نشط</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">وضع مؤقت للمحاضرات</label>
                  <select
                    value={course?.isOpenToAll ? 'true' : 'false'}
                    onChange={handleInputChange}
                    name="isOpenToAll"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  >
                    <option value="false">نعم</option>
                    <option value="true">لا</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">كورس مقترح</label>
                  <select
                    value={course?.isSuggested ? 'true' : 'false'}
                    onChange={handleInputChange}
                    name="isSuggested"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  >
                    <option value="false">لا</option>
                    <option value="true">نعم</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">السعر</label>
                <input
                  type="number"
                  value={course?.price || 0}
                  onChange={handleNumberChange((value) => course && setCourse({ ...course, price: value }))}
                  name="price"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">الوصف</label>
                <textarea
                  value={course?.description || ''}
                  onChange={handleTextChange((value) => course && setCourse({ ...course, description: value }))}
                  name="description"
                  rows={4}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  placeholder="أدخل وصف الدورة"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">صورة الدورة</label>
                <div className="mt-1 flex flex-col items-center">
                  {!course?.imagePath && !previewUrl ? (
                    // Show upload section only if no image exists
                    <div className="w-full flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                      <div className="space-y-1 text-center">
                        <svg
                          className="mx-auto h-12 w-12 text-gray-400"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 48 48"
                          aria-hidden="true"
                        >
                          <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <div className="flex text-sm text-gray-600">
                          <label
                            htmlFor="image-upload"
                            className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary"
                          >
                            <span>تحميل صورة</span>
                            <input
                              id="image-upload"
                              name="ImageFile"
                              type="file"
                              accept="image/*"
                              className="sr-only"
                              onChange={handleImageChange}
                            />
                          </label>
                          <p className="pr-2">أو اسحب وأفلت</p>
                        </div>
                        <p className="text-xs text-gray-500">PNG, JPG, GIF حتى 5MB</p>
                      </div>
                    </div>
                  ) : (
                    // Show existing image with option to remove
                    <div className="mt-4 w-full">
                      <div className="relative">
                        <img
                          src={previewUrl || (course?.imagePath ? getImageUrl(course.imagePath) : undefined)}
                          alt={course?.courseName || 'Course Image'}
                          className="w-full max-h-64 rounded-md border border-gray-200 object-contain"
                          onError={(e: any) => {
                            const imgElement = e.target as HTMLImageElement;
                            if (imgElement) {
                              imgElement.src = 'https://via.placeholder.com/300x200?text=Image+Not+Found';
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="absolute top-2 right-2 p-1.5 rounded-full bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
                          onClick={() => {
                            setImageFile(null);
                            setPreviewUrl(null);
                            if (course?.imagePath) {
                              setCourse({ ...course, imagePath: null });
                            }
                          }}
                          title="حذف الصورة"
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {imageError && (
                    <p className="mt-2 text-sm text-red-600">{imageError}</p>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">آخر تعديل</label>
                <p className="mt-1 text-sm text-gray-500">
                  {course?.modificationDate ? new Date(course.modificationDate).toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : 'غير محدد'}
                </p>
              </div>

              <div className="flex justify-start gap-4">
                <button
                  type="submit"
                  onClick={handleSaveCourse}
                  disabled={isSaving}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center"
                >
                  {isSaving ? (
                    <>
                      <svg className="animate-spin -mr-1 ml-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      جاري الحفظ...
                    </>
                  ) : (
                    <>
                      <CheckIcon className="h-4 w-4 ml-2" />
                      Save Changes
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/courses')}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  إلغاء
                </button>
              </div>
            </form>
              </>
            )}
          </div>
        )}

        {activeTab === 'students' && (
          <div className="bg-white shadow rounded-lg p-6">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-800">الطلاب المسجلون</h2>
            </div>
            
            {/* Add filter section */}
            <div className="mb-6 bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    البريد الإلكتروني أو الجوال
                  </label>
                  <input
                    type="text"
                    name="searchTerm"
                    value={studentFilters.searchTerm}
                    onChange={handleStudentFilterChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="ابحث بالبريد الإلكتروني أو الجوال"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    الاسم
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={studentFilters.name}
                    onChange={handleStudentFilterChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="ابحث بالاسم"
                  />
                </div>
                <div className="flex items-end space-x-2">
                  <button
                    onClick={applyStudentFilters}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    تطبيق 
                  </button>
                  <button
                    onClick={resetStudentFilters}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    إعادة تعيين
                  </button>
                </div>
              </div>
            </div>
            
            {studentsError && (
              <div className="mb-6 bg-red-50 p-4 rounded-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <p className="text-sm text-red-700 mt-2">{studentsError}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Students table header with count and enroll button */}
            <div className="flex justify-between items-center mb-4">
              {canManageEnrollment && (
              <button
                onClick={() => setShowEnrollStudentModal(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                تسجيل طالب
              </button>
              )}
              <h3 className="text-lg font-semibold">
                {canViewStudentCounts ? `الإجمالي: ${studentTotalCount}` : 'الطلاب المسجلون'}
              </h3>
            </div>
            
            {loadingStudents ? (
              <div className="flex justify-center items-center py-12">
                <svg className="animate-spin h-8 w-8 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            ) : enrolledStudents.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Academic Year</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone Number</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enrollment Date</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {/* Apply client-side pagination */}
                    {enrolledStudents
                      .slice(
                        (studentCurrentPage - 1) * studentPageSize,
                        studentCurrentPage * studentPageSize
                      )
                      .map((student) => (
                      <tr key={student.id} className="hover:bg-gray-50 transition-colors duration-200">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div 
                            className="font-medium text-indigo-600 cursor-pointer hover:text-indigo-900" 
                            dir="auto"
                            onClick={() => navigate(`/students/${student.id}`)}
                          >
                            {student.userName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {student.academicYear}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" dir="ltr">
                          {student.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {student.phonenumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(student.enrollmentDate).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            student.active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            <span className={`h-1.5 w-1.5 mr-1.5 rounded-full ${
                              student.active ? 'bg-green-400' : 'bg-red-400'
                            }`}></span>
                            {student.active ? 'Active' : 'Blocked'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <button
                            onClick={() => {
                              setStudentToRemove(student);
                              setShowRemoveStudentModal(true);
                            }}
                            className="text-white bg-red-600 hover:bg-red-700 px-3 py-1 rounded text-sm"
                          >
                            إزالة
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                                      <h3 className="mt-2 text-sm font-medium text-gray-900">لا يوجد طلاب مسجلون</h3>
                      <p className="mt-1 text-sm text-gray-500">لا يوجد طلاب مسجلون حاليًا في هذه الدورة.</p>
              </div>
            )}
            
            {/* Pagination for students */}
            {studentTotalPages > 1 && (
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                <nav className="relative z-0 inline-flex rounded-md shadow-sm" aria-label="التنقل بين الصفحات">
                  <button
                    onClick={() => handleStudentPageChange(studentCurrentPage - 1)}
                    disabled={studentCurrentPage === 1}
                    className={`relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                      studentCurrentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="sr-only">السابق</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {/* Show limited page buttons with ellipsis for many pages */}
                  {(() => {
                    const visiblePageCount = 5; // Number of page buttons to show
                    const halfVisible = Math.floor(visiblePageCount / 2);
                    let startPage = Math.max(1, studentCurrentPage - halfVisible);
                    let endPage = Math.min(studentTotalPages, startPage + visiblePageCount - 1);
                    
                    // Adjust if we're near the end
                    if (endPage - startPage + 1 < visiblePageCount) {
                      startPage = Math.max(1, endPage - visiblePageCount + 1);
                    }
                    
                    const pages = [];
                    
                    // Add first page and ellipsis if needed
                    if (startPage > 1) {
                      pages.push(
                        <button
                          key="1"
                          onClick={() => handleStudentPageChange(1)}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                          1
                        </button>
                      );
                      if (startPage > 2) {
                        pages.push(
                          <span key="start-ellipsis" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                            ...
                          </span>
                        );
                      }
                    }
                    
                    // Add visible page buttons
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => handleStudentPageChange(i)}
                          className={`relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium ${
                            i === studentCurrentPage
                              ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    }
                    
                    // Add last page and ellipsis if needed
                    if (endPage < studentTotalPages) {
                      if (endPage < studentTotalPages - 1) {
                        pages.push(
                          <span key="end-ellipsis" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                            ...
                          </span>
                        );
                      }
                      pages.push(
                        <button
                          key={studentTotalPages}
                          onClick={() => handleStudentPageChange(studentTotalPages)}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                          {studentTotalPages}
                        </button>
                      );
                    }
                    
                    return pages;
                  })()}
                  
                  <button
                    onClick={() => handleStudentPageChange(studentCurrentPage + 1)}
                    disabled={studentCurrentPage === studentTotalPages}
                    className={`relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                      studentCurrentPage === studentTotalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="sr-only">التالي</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                                  </nav>
                  <div>
                    <p className="text-sm text-gray-700">
                      {canViewStudentCounts ? (
                        <>
                          Showing <span className="font-medium">{((studentCurrentPage - 1) * studentPageSize) + 1}</span> to{' '}
                          <span className="font-medium">{Math.min(studentCurrentPage * studentPageSize, studentTotalCount)}</span> of{' '}
                          <span className="font-medium">{studentTotalCount}</span> students
                        </>
                      ) : (
                        <>
                          الصفحة <span className="font-medium">{studentCurrentPage}</span>
                        </>
                      )}
                    </p>
                  </div>
                </div>
              )}
          </div>
        )}
      </div>

      {/* Side Panel for Lesson Details */}
      {showSidePanel && sidePanelContent && (
        <div className="fixed inset-y-0 left-0 w-1/2 bg-white shadow-lg">
          <div className="h-full flex flex-col">
            <div className="p-6 border-b flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold mb-1">{sidePanelContent.title}</h2>
                <p className="text-sm text-gray-500">
                  {sidePanelContent.type === 'video' ? 'درس فيديو' : 'اختبار'}
                </p>
              </div>
              <button
                onClick={() => setShowSidePanel(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-6">
              {sidePanelContent.type === 'video' ? (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lesson Title
                    </label>
                    <input
                      type="text"
                      value={sidePanelContent.title}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setSidePanelContent({
                          ...sidePanelContent,
                          title: e.target.value
                        });
                      }}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>

                  <div className="border-t pt-6">
                    <h3 className="font-medium mb-4">تفاصيل الفيديو</h3>
                    <div className="space-y-4">
                      <div>
                                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                            رابط الفيديو
                          </label>
                        <input
                          type="url"
                          value={videoUrl}
                          onChange={handleVideoUrlChange}
                          className="w-full px-3 py-2 border rounded-lg"
                          placeholder="https://youtube.com/watch?v=..."
                        />
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-medium">المرفقات</h3>
                    </div>
                    
                    {/* Existing Attachments */}
                    <div className="space-y-3 mb-4">
                      {attachments.map((attachment) => (
                        <div key={attachment.id} className="flex items-center gap-2 p-3 border rounded-lg">
                          <div className="flex-1">
                            <p className="font-medium">{attachment.title}</p>
                            <p className="text-sm text-gray-500 truncate">{attachment.url}</p>
                          </div>
                          <button
                            onClick={() => handleRemoveAttachment(attachment.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                    </div>

                    {/* New Attachment Form */}
                    <div className="border rounded-lg p-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          عنوان المرفق
                        </label>
                        <input
                          type="text"
                          value={newAttachment.title}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                            setNewAttachment({ ...newAttachment, title: e.target.value })
                          }
                          className="w-full px-3 py-2 border rounded-lg"
                          placeholder="أدخل عنوان المرفق"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          رابط المرفق
                        </label>
                        <input
                          type="url"
                          value={newAttachment.url}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                            setNewAttachment({ ...newAttachment, url: e.target.value })
                          }
                          className="w-full px-3 py-2 border rounded-lg"
                          placeholder="https://..."
                        />
                      </div>
                      <button
                        onClick={handleAddAttachment}
                        className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        إضافة مرفق
                      </button>
                    </div>
                  </div>

                  <div className="border-t pt-6 flex justify-start gap-4">
                    <button
                      className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      Save Changes
                    </button>
                    <button
                      onClick={() => setShowSidePanel(false)}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      إلغاء
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quiz Title
                    </label>
                    <input
                      type="text"
                      value={sidePanelContent.title}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setSidePanelContent({
                          ...sidePanelContent,
                          title: e.target.value
                        });
                      }}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>

                  {/* Questions List */}
                  <div className="space-y-4">
                    <h3 className="font-medium">Quiz Questions</h3>
                    {questions.length === 0 ? (
                      <p className="text-gray-500 italic">No questions added yet.</p>
                    ) : (
                      questions.map((question, index) => (
                        <div key={question.id} className="border rounded-lg p-4">
                          <div className="flex justify-between items-start mb-3">
                            <h4 className="font-medium">Question {index + 1}</h4>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditQuestion(question)}
                                className="text-blue-600 hover:text-blue-700"
                                title="Edit Question"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDeleteQuestion(question.id)}
                                className="text-red-600 hover:text-red-700"
                                title="Delete Question"
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                          <p className="mb-3">{question.text}</p>
                          <div className="grid grid-cols-2 gap-2">
                            {question.options.map((option, optIndex) => (
                              <div
                                key={optIndex}
                                className={`p-2 border rounded ${
                                  question.correctOptionIndex === optIndex
                                    ? 'bg-green-50 border-green-500'
                                    : ''
                                }`}
                              >
                                {String.fromCharCode(65 + optIndex)}. {option}
                                {question.correctOptionIndex === optIndex && (
                                  <span className="ml-2 text-green-600">✓</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <button
                    onClick={() => {
                      setEditingQuestion(null);
                      setCurrentQuestion({
                        id: questions.length > 0 ? Math.max(...questions.map(q => q.id)) + 1 : 1,
                        text: '',
                        options: ['', '', '', ''],
                              correctOptionIndex: null,
                              type: 'MultipleChoice'
                      });
                      setShowQuestionForm(true);
                    }}
                    className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Add Question
                  </button>

                  <div className="border-t pt-6 flex justify-start gap-4">
                    <button
                      className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      Save Changes
                    </button>
                    <button
                      onClick={() => setShowSidePanel(false)}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      إلغاء
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Unit Modal */}
      {showAddUnitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-4">Add New Unit</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unit Title
              </label>
              <input
                type="text"
                value={newUnitTitle}
                onChange={handleTextChange(setNewUnitTitle)}
                className="w-full px-3 py-2 border rounded-lg"
                                  placeholder="أدخل عنوان المحاضرة..."
              />
            </div>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowAddUnitModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={handleAddUnit}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              >
                إضافة وحدة
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lesson Type Modal */}
      {showLessonTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-6 text-center">اختر نوع الدرس</h2>
              <div className="grid grid-cols-3 gap-6">
              <div 
                onClick={handleAddVideoLesson}
                className="border rounded-lg p-4 flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-gray-50"
              >
                <div className="bg-blue-100 p-3 rounded-full">
                  <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="font-medium">درس فيديو</span>
              </div>
              <div 
                onClick={handleAddQuizLesson}
                className="border rounded-lg p-4 flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-gray-50"
              >
                <div className="bg-green-100 p-3 rounded-full">
                  <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="font-medium">اختبار</span>
              </div>
              <div 
                onClick={handleAddFileLesson}
                className="border rounded-lg p-4 flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-gray-50"
              >
                <div className="bg-red-100 p-3 rounded-full">
                  <svg className="w-10 h-10 text-red-600" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                  </svg>
                </div>
                <span className="font-medium">PDF</span>
              </div>
              <div 
                onClick={() => {
                  setShowLessonTypeModal(false);
                  const selectedUnit = units[selectedUnitIndex!];
                  if (selectedUnit) {
                    navigate(`/assignment/${courseId}/${selectedUnit.id}`);
                  } else {
                    navigate(`/assignment/${courseId}`);
                  }
                }}
                className="border rounded-lg p-4 flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-gray-50"
              >
                <div className="bg-yellow-100 p-3 rounded-full">
                  <svg className="w-10 h-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <span className="font-medium">واجب</span>
              </div>
              {/* <div 
                onClick={() => {
                  const selectedUnit = units[selectedUnitIndex!];
                  if (selectedUnit) {
                    navigate(`/random-quiz/${courseId}/${selectedUnit.id}`);
                  } else {
                    navigate(`/random-quiz/${courseId}`);
                  }
                }}
                className="border rounded-lg p-4 flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-gray-50"
              >
                <div className="bg-purple-100 p-3 rounded-full">
                  <svg className="w-10 h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <span className="font-medium">امتحان من بنك الأسئلة</span>
              </div> */}
            </div>
            <div className="mt-6 flex justify-center">
              <button
                onClick={() => setShowLessonTypeModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Lesson Form */}
      {showVideoLessonForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full">
            <h2 className="text-2xl font-bold mb-6">إضافة درس فيديو</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  عنوان الدرس *
                </label>
                <input
                  type="text"
                  value={videoLessonTitle}
                  onChange={handleTextChange(setVideoLessonTitle)}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="أدخل عنوان الدرس..."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  رابط الفيديو (يوتيوب) *
                </label>
                <input
                  type="url"
                  value={videoUrl}
                  onChange={handleVideoUrlChange}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="https://youtube.com/watch?v=..."
                  required
                />
              </div>
              <div className="border-t pt-4 mt-4">
                <h3 className="font-medium mb-2">مرفق (اختياري)</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Attachment Title
                    </label>
                    <input
                      type="text"
                      value={attachmentTitle}
                      onChange={handleAttachmentTitleChange}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="أدخل عنوان المرفق..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      رابط المرفق
                    </label>
                    <input
                      type="url"
                      value={attachmentUrl}
                      onChange={handleAttachmentUrlChange}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={() => setShowVideoLessonForm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={handleCreateVideoLesson}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              >
                إنشاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quiz Form */}
      {showQuizForm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center overflow-y-auto">
                <div className="bg-white rounded-lg p-6 max-w-2xl w-full my-8 max-h-[80vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">إنشاء اختبار</h2>
                  <div className="space-y-6">
                    <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quiz Title *
              </label>
              <input
                type="text"
                value={quizTitle}
                onChange={handleQuizTitleChange}
                className="w-full px-3 py-2 border rounded-lg"
                                  placeholder="أدخل عنوان الاختبار..."
                required
              />
            </div>

                    {/* Import existing quiz questions from another lesson */}
                    <div className="border border-blue-100 rounded-lg p-4 bg-blue-50/40">
                      <h3 className="text-md font-semibold text-gray-900 mb-3">
                        استيراد أسئلة من اختبار موجود (اختياري)
                      </h3>
                      <p className="text-sm text-gray-600 mb-4">
                        يمكنك اختيار دورة ووحدة واختبار سبق إنشاؤه لنسخ الأسئلة إلى هذا الاختبار الجديد.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            اختر الدورة:
                          </label>
                          <select
                            value={selectedImportCourseId}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                              const value = e.target.value;
                              setSelectedImportCourseId(value);
                              fetchImportUnitsForCourse(value);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            disabled={importCoursesLoading}
                          >
                            <option value="">
                              {importCoursesLoading ? 'جاري تحميل الدورات...' : 'اختر الدورة'}
                            </option>
                            {importCourses.map(course => (
                              <option key={course.id} value={course.id}>
                                {course.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            اختر الوحدة (من الدورة المحددة):
                          </label>
                          <select
                            value={selectedImportUnitId || ''}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                              setSelectedImportUnitId(Number(e.target.value) || null)
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            disabled={!selectedImportCourseId || importUnits.length === 0}
                          >
                            <option value="">اختر الوحدة</option>
                            {importUnits.map(unit => (
                              <option key={unit.id} value={unit.id}>
                                {unit.title}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            اختر الاختبار (الدرس):
                          </label>
                          <select
                            value={selectedImportQuizId || ''}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                              setSelectedImportQuizId(Number(e.target.value) || null)
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            disabled={!selectedImportUnitId || importQuizzes.length === 0}
                          >
                            <option value="">اختر الاختبار</option>
                            {importQuizzes.map(lesson => (
                              <option key={lesson.id} value={lesson.id}>
                                {lesson.title}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="flex justify-end mt-4">
                        <button
                          onClick={handleImportQuizIntoForm}
                          disabled={
                            importingQuiz ||
                            !selectedImportCourseId ||
                            !selectedImportUnitId ||
                            !selectedImportQuizId
                          }
                          className={`px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors ${
                            importingQuiz ||
                            !selectedImportCourseId ||
                            !selectedImportUnitId ||
                            !selectedImportQuizId
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-emerald-600 hover:bg-emerald-700'
                          }`}
                        >
                          {importingQuiz ? 'جاري استيراد الأسئلة...' : 'استيراد الأسئلة من اختبار'}
                        </button>
                      </div>
                    </div>

                    {/* Questions List */}
                    <div>
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium">الأسئلة</h3>
              <button
                          onClick={() => {
                            setEditingQuestion(null);
                            setCurrentQuestion({
                              id: questions.length > 0 ? Math.max(...questions.map(q => q.id)) + 1 : 1,
                              text: '',
                              options: ['', '', '', ''],
                              correctOptionIndex: null,
                              type: 'MultipleChoice'
                            });
                            setShowQuestionForm(true);
                          }}
                          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                        >
                          إضافة سؤال
                        </button>
                      </div>

                      {questions.length === 0 ? (
                        <div className="text-center py-8 bg-gray-50 rounded-lg">
                          <p className="text-gray-500">لم تتم إضافة أي أسئلة بعد. انقر على "إضافة سؤال" لإنشاء أول سؤال.</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {questions.map((question, index) => (
                            <div key={question.id} className="border rounded-lg p-4">
                              <div className="flex justify-between items-start mb-3">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium">Question {index + 1}</h4>
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                    {question.type === 'TrueFalse' ? 'صح/خطأ' : 'اختيار من متعدد'}
                                  </span>
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleEditQuestion(question)}
                                    className="text-blue-600 hover:text-blue-700"
                                    title="Edit Question"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => handleDeleteQuestion(question.id)}
                                    className="text-red-600 hover:text-red-700"
                                    title="Delete Question"
                                  >
                                    <TrashIcon className="w-5 h-5" />
                                  </button>
                                </div>
                              </div>
                              <p className="mb-3">{question.text}</p>
                              <div className="grid grid-cols-2 gap-2">
                                {question.options.map((option, optIndex) => (
                                  <div
                                    key={optIndex}
                                    className={`p-2 border rounded ${
                                      question.correctOptionIndex === optIndex
                                        ? 'bg-green-50 border-green-500'
                                        : ''
                                    }`}
                                  >
                                    {String.fromCharCode(65 + optIndex)}. {option}
                                    {question.correctOptionIndex === optIndex && (
                                      <span className="ml-2 text-green-600">✓</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end gap-4 mt-6">
                    <button
                      onClick={() => {
                        setShowQuizForm(false);
                        setQuizTitle('');
                        setQuestions([]);
                      }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                إلغاء
              </button>
                                      <button
                          onClick={handleCreateQuiz}
                          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                        >
                          إنشاء اختبار
                        </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Lesson Form */}
      {showFileLessonForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full my-8 max-h-[80vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">إضافة درس PDF</h2>
            <div className="space-y-6">
              {/* Lesson Name Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  عنوان الدرس *
                </label>
                <input
                  type="text"
                  value={fileLessonTitle}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFileLessonTitle(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="أدخل عنوان الدرس..."
                  required
                />
              </div>

              {/* File Inputs */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  اسم ملف PDF *
                </label>
                <input
                  type="text"
                  value={newFileName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewFileName(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg mb-4"
                  placeholder="مثال: document1.pdf"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  رابط ملف PDF *
                </label>
                <input
                  type="url"
                  value={newFileUrl}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewFileUrl(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg mb-4"
                  placeholder="https://example.com/files/doc1.pdf"
                />
              </div>

              {/* Add File Button - Only show if there are already files added */}
              {lessonFiles.length > 0 && (
                <div className="flex justify-center">
                  <button
                    onClick={handleAddFile}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg transition-colors ${
                      !newFileName.trim() || !newFileUrl.trim()
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    }`}
                    disabled={!newFileName.trim() || !newFileUrl.trim()}
                  >
                    <PlusIcon className="w-5 h-5" />
                    <span>إضافة ملف PDF آخر</span>
                  </button>
                </div>
              )}

              {/* Added Files List */}
              {lessonFiles.length > 0 && (
                <div className="border-t pt-4 mt-4">
                  <h3 className="text-lg font-medium mb-4">الملفات المضافة ({lessonFiles.length})</h3>
                  <div className="space-y-3">
                    {lessonFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border">
                        <div className="flex items-center gap-3 flex-1">
                          <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{file.fileName}</p>
                            <p className="text-xs text-gray-500 truncate">{file.fileUrl}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveFile(index)}
                          className="text-red-600 hover:text-red-700 ml-3"
                          title="حذف الملف"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={() => {
                  setShowFileLessonForm(false);
                  setFileLessonTitle('');
                  setLessonFiles([]);
                  setNewFileName('');
                  setNewFileUrl('');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={handleCreateFileLesson}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!fileLessonTitle.trim() || (!newFileName.trim() && !newFileUrl.trim() && lessonFiles.length === 0)}
              >
                إنشاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Question Form Modal */}
      {showQuestionForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">{editingQuestion ? 'Edit Question' : 'Add New Question'}</h2>
            <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Question Type *
                      </label>
                      <select
                        value={currentQuestion.type}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                          const newType = e.target.value as 'MultipleChoice' | 'TrueFalse';
                          setCurrentQuestion({
                            ...currentQuestion,
                            type: newType,
                            options: newType === 'TrueFalse' ? ['True', 'False'] : ['', '', '', ''],
                            correctOptionIndex: null
                          });
                        }}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        <option value="MultipleChoice">Multiple Choice</option>
                        <option value="TrueFalse">True/False</option>
                      </select>
                    </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Question Text *
                </label>
                <textarea
                  value={currentQuestion.text}
                  onChange={handleQuestionChange}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="أدخل نص السؤال..."
                  rows={2}
                  required
                />
              </div>
              
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                        {currentQuestion.type === 'TrueFalse' ? 'Select the correct answer' : 'Options * (Select the correct answer)'}
                </label>
                
                {currentQuestion.options.map((option, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <input
                        type="radio"
                        name="correctOption"
                        checked={currentQuestion.correctOptionIndex === index}
                        onChange={() => handleCorrectAnswerSelect(index)}
                        className="w-4 h-4 text-primary focus:ring-primary"
                      />
                    </div>
                    <div className="flex-grow">
                      <div className="flex items-center">
                        <span className="w-6 text-gray-700">{String.fromCharCode(65 + index)}.</span>
                              {currentQuestion.type === 'TrueFalse' ? (
                                <div className="px-3 py-2">{option}</div>
                              ) : (
                        <input
                          type="text"
                          value={option}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOptionChange(index, e)}
                          className="w-full px-3 py-2 border rounded-lg"
                          placeholder={`Option ${String.fromCharCode(65 + index)}`}
                          required
                        />
                              )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={() => {
                  setShowQuestionForm(false);
                  setEditingQuestion(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={handleUpdateQuestion}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              >
                {editingQuestion ? 'Update Question' : 'Add Question'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Unit Confirmation Modal */}
      {showDeleteUnitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4">حذف المحاضرة</h2>
            <p className="mb-6">
              هل أنت متأكد أنك تريد حذف هذه المحاضرة لا يمكن التراجع عن هذا الإجراء وسيتم حذف جميع الدروس داخل هذه المحاضرة نهائيًا.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowDeleteUnitModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={confirmDeleteUnit}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                حذف
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hide Unit Confirmation Modal */}
      {showHideUnitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4">
              {unitToAction && units.find(u => u.id === unitToAction.id)?.isVisible
                ? "إخفاء المحاضرة"
                : "إظهار المحاضرة"
              }
            </h2>
            <p className="mb-6">
              {unitToAction && units.find(u => u.id === unitToAction.id)?.isVisible
                ? "Are you sure you want to hide this unit? Students will not be able to see this unit and its content until you make it visible again."
                : "Are you sure you want to make this unit visible to students?"
              }
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowHideUnitModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={confirmHideUnit}
                className={`px-4 py-2 ${
                  unitToAction && units.find(u => u.id === unitToAction.id)?.isVisible
                    ? "bg-yellow-600 hover:bg-yellow-700"
                    : "bg-green-600 hover:bg-green-700"
                } text-white rounded-lg transition-colors`}
              >
                {unitToAction && units.find(u => u.id === unitToAction.id)?.isVisible
                  ? "Hide"
                  : "Show"
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Lesson Confirmation Modal */}
      {showDeleteLessonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4">حذف الدرس</h2>
            <p className="mb-6">
              هل أنت متأكد أنك تريد حذف هذا الدرس؟ لا يمكن التراجع عن هذا الإجراء وسيتم حذف جميع المحتويات المرتبطة نهائيًا.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowDeleteLessonModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={confirmDeleteLesson}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                حذف
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hide Lesson Confirmation Modal */}
      {showHideLessonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4">
              {lessonToAction && units[lessonToAction.unitIndex]?.lessons.find(l => l.id === lessonToAction.lessonId)?.isVisible
                ? "إخفاء الدرس"
                : "إظهار الدرس"
              }
            </h2>
            <p className="mb-6">
              {lessonToAction && units[lessonToAction.unitIndex]?.lessons.find(l => l.id === lessonToAction.lessonId)?.isVisible
                ? "Are you sure you want to hide this lesson? Students will not be able to see this lesson until you make it visible again."
                : "Are you sure you want to make this lesson visible to students?"
              }
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowHideLessonModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={confirmHideLesson}
                className={`px-4 py-2 ${
                  lessonToAction && units[lessonToAction.unitIndex]?.lessons.find(l => l.id === lessonToAction.lessonId)?.isVisible
                    ? "bg-yellow-600 hover:bg-yellow-700"
                    : "bg-green-600 hover:bg-green-700"
                } text-white rounded-lg transition-colors`}
              >
                {lessonToAction && units[lessonToAction.unitIndex]?.lessons.find(l => l.id === lessonToAction.lessonId)?.isVisible
                  ? "Hide"
                  : "Show"
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enroll Student Modal */}
      {showEnrollStudentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">تسجيل طالب</h2>
            
            {enrollStudentError && (
              <div className="mb-4 p-3 bg-red-50 text-sm text-red-700 rounded-md">
                {enrollStudentError}
              </div>
            )}
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                رقم الجوال
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={enrollStudentData}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setEnrollStudentData(e.target.value);
                    // Clear previous search results when input changes
                    setFoundStudent(null);
                    setSearchSuccessMessage(null);
                    setEnrollStudentError(null);
                  }}
                  className="flex-1 px-3 py-2 border rounded-lg"
                  placeholder="أدخل رقم جوال الطالب"
                />
                <button
                  onClick={handleSearchStudent}
                  disabled={searchingStudent || !enrollStudentData.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {searchingStudent ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </span>
                  ) : 'Search'}
                </button>
              </div>
              
              {searchSuccessMessage && (
                <div className="mt-2 p-2 bg-green-50 text-sm text-green-700 rounded-md">
                  {searchSuccessMessage}
                </div>
              )}
              
              {foundStudent && (
                <div className="mt-4 p-3 border rounded-lg bg-gray-50">
                  <h3 className="text-sm font-semibold mb-2">Student Details</h3>
                  <p><span className="font-medium">Name:</span> {foundStudent.userName}</p>
                  <p><span className="font-medium">Email:</span> {foundStudent.email}</p>
                  <p><span className="font-medium">Phone:</span> {foundStudent.phonenumber}</p>
                  <p><span className="font-medium">Academic Year:</span> {foundStudent.academicYear}</p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-4">
              <button
                onClick={() => {
                  setShowEnrollStudentModal(false);
                  setEnrollStudentData('');
                  setEnrollStudentError(null);
                  setFoundStudent(null);
                  setSearchSuccessMessage(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={handleEnrollStudent}
                disabled={isEnrollingStudent || !foundStudent}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {isEnrollingStudent ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    جاري التسجيل...
                  </span>
                ) : 'Enroll'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Remove Student Confirmation Modal */}
      {showRemoveStudentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">إزالة طالب</h2>
            <p className="mb-6">
              هل أنت متأكد أنك تريد إزالة <span className="font-semibold">{studentToRemove?.userName}</span> من هذه الدورة؟ لا يمكن التراجع عن هذا الإجراء.
            </p>
            
            <div className="flex justify-end gap-4">
              <button
                onClick={() => {
                  setShowRemoveStudentModal(false);
                  setStudentToRemove(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={handleRemoveStudent}
                disabled={isRemovingStudent}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {isRemovingStudent ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    جاري الحذف...
                  </span>
                ) : 'إزالة'}
              </button>
            </div>
          </div>
        </div>
            )}

        {/* Edit Unit Modal */}
        {showEditUnitModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h2 className="text-xl font-semibold mb-4">تعديل المحاضرة</h2>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Unit Name
                </label>
                <input
                  type="text"
                  value={editUnitName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditUnitName(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="أدخل اسم المحاضرة"
                />
              </div>
              
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setShowEditUnitModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  disabled={isSaving}
                >
                  إلغاء
                </button>
                <button
                  onClick={confirmEditUnit}
                  disabled={!editUnitName.trim() || isSaving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {isSaving ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      جاري الحفظ...
                    </span>
                  ) : 'حفظ التغييرات'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Quiz Results Modal */}
        {showQuizResultsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">نتائج الاختبار</h2>
                <button
                  onClick={() => setShowQuizResultsModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
              
              {loadingQuizResults ? (
                <div className="flex justify-center items-center py-12">
                  <svg className="animate-spin h-8 w-8 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              ) : quizResultsError ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">خطأ في تحميل نتائج الاختبار</h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>{quizResultsError}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : quizResults ? (
                <div>
                  <div className="mb-6 bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-medium text-lg mb-2">{quizResults.lessonName}</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-gray-600">Total Submissions:</span>
                        <span className="ml-2 font-semibold">{quizResults.totalSubmissions}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Average Score:</span>
                        <span className="ml-2 font-semibold">{quizResults.averageScore.toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                  
                  {quizResults.results.length > 0 ? (
                    <div>
                      <div className="mb-4 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => toggleQuizResultsSort('score')}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            quizResultsSort.score
                              ? 'bg-indigo-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          ترتيب الدرجات من الأعلى للأقل
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleQuizResultsSort('date')}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            quizResultsSort.date
                              ? 'bg-indigo-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          ترتيب تاريخ التسليم
                        </button>
                      </div>
                      <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">الطالب</th>
                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">الدرجة</th>
                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">تاريخ التسليم</th>
                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">ملاحظات</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {getSortedQuizResults().map((result) => (
                            <tr key={result.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="font-medium text-gray-900">{result.userName}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  result.score >= 70 
                                    ? 'bg-green-100 text-green-800' 
                                    : result.score >= 50 
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-red-100 text-red-800'
                                }`}>
                                  {result.score.toFixed(1)}%
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {new Date(result.submittedAt).toLocaleString('ar-EG', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  second: '2-digit'
                                })}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {result.notes || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900">لا توجد نتائج بعد</h3>
                      <p className="mt-1 text-sm text-gray-500">لم يقم أي طالب بحل هذا الاختبار بعد.</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p>لا توجد بيانات متاحة</p>
                </div>
              )}
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowQuizResultsModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  إغلاق
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Student Completion Status Modal */}
        {showStudentCompletionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">حالة مشاهدة الطلاب للفيديو</h2>
                <button
                  onClick={() => setShowStudentCompletionModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
              
              {loadingStudentCompletion ? (
                <div className="flex justify-center items-center py-12">
                  <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              ) : studentCompletionError ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">خطأ في تحميل بيانات الطلاب</h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>{studentCompletionError}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : studentCompletion ? (
                <div className="space-y-8">
                  {/* Completed Students Section */}
                  <div>
                    <div className="mb-4 flex items-center gap-3">
                      <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                      <h3 className="text-lg font-semibold text-green-700">
                        الطلاب الذين شاهدوا الفيديو ({studentCompletion.completedStudents.length})
                      </h3>
                    </div>
                    
                    {studentCompletion.completedStudents.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-green-50">
                            <tr>
                              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-green-700 uppercase tracking-wider">
                                اسم الطالب
                              </th>
                              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-green-700 uppercase tracking-wider">
                                رقم الطالب
                              </th>
                              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-green-700 uppercase tracking-wider">
                                رقم ولي الأمر
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {studentCompletion.completedStudents.map((student) => (
                              <tr key={student.studentId} className="hover:bg-green-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <button
                                    onClick={() => navigate(`/students/${student.studentId}`)}
                                    className="font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors cursor-pointer"
                                  >
                                    {student.studentName}
                                  </button>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                  {student.studentPhone}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                  {student.parentPhone}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-green-50 rounded-lg">
                        <div className="mx-auto h-12 w-12 text-green-400 mb-3">
                          <CheckIcon />
                        </div>
                        <h3 className="text-sm font-medium text-green-700">لا يوجد طلاب</h3>
                        <p className="text-sm text-green-600">لم يشاهد أي طالب هذا الفيديو بعد.</p>
                      </div>
                    )}
                  </div>

                  {/* Not Completed Students Section */}
                  <div>
                    <div className="mb-4 flex items-center gap-3">
                      <div className="w-4 h-4 bg-gray-400 rounded-full"></div>
                      <h3 className="text-lg font-semibold text-gray-600">
                        الطلاب الذين لم يشاهدوا الفيديو ({studentCompletion.notCompletedStudents.length})
                      </h3>
                    </div>
                    
                    {studentCompletion.notCompletedStudents.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                اسم الطالب
                              </th>
                              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                رقم الطالب
                              </th>
                              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                رقم ولي الأمر
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {studentCompletion.notCompletedStudents.map((student) => (
                              <tr key={student.studentId} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <button
                                    onClick={() => navigate(`/students/${student.studentId}`)}
                                    className="font-medium text-blue-400 hover:text-blue-600 hover:underline transition-colors cursor-pointer"
                                  >
                                    {student.studentName}
                                  </button>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                                  {student.studentPhone}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                                  {student.parentPhone}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <div className="mx-auto h-12 w-12 text-gray-400 mb-3">
                          <CheckIcon />
                        </div>
                        <h3 className="text-sm font-medium text-gray-600">جميع الطلاب شاهدوا الفيديو</h3>
                        <p className="text-sm text-gray-500">ممتاز! جميع الطلاب أكملوا مشاهدة هذا الفيديو.</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p>لا توجد بيانات متاحة</p>
                </div>
              )}
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowStudentCompletionModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  إغلاق
                </button>
              </div>
            </div>
          </div>
        )}
          </>
      )}
      </div>
    </div>
  );
};

export default CourseDetail; 